import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, FolderOpen, Building2, BarChart3, Search, Phone, Mail, Navigation,
  CheckCircle, Clock, AlertTriangle, XCircle, Euro, TrendingUp, 
  LogOut, RefreshCw, Eye, EyeOff, MessageCircle, Send, Plus, Trash2,
  Settings, CreditCard, ExternalLink, Loader2, Shield, Upload, X, Palette, Camera,
  ChevronDown, ChevronRight, Headphones, Home, Copy, ArrowUpRight, ArrowLeft,
  Calendar, MapPin, Award, StickyNote, Receipt, FileText, Download, Archive,
  UserCheck, Target, Menu, Sparkles, Calculator, Save, Globe, DollarSign, Printer, QrCode, KeyRound
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { clearPartner } from '../lib/partnerContext';
import PartnerEstimationCoefsTab from '../components/partner/PartnerEstimationCoefsTab';
import ProjectCommunication from '../components/ProjectCommunication';
import Pagination, { usePagination } from '../components/admin/Pagination';
import KitPubSection from '../components/KitPubSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  in_review: { label: 'En cours', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  nouveau: { label: 'Nouveau', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  contacte: { label: 'Contacté', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  vu: { label: 'Vu', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  en_cours: { label: 'En cours', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  traite: { label: 'Traité', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  termine: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  paid: { label: 'Payé', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  pending: { label: 'En attente', color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-500' },
};

// Sidebar Navigation (same structure as Admin)
const NAV_CATEGORIES = [
  {
    id: 'operations',
    label: 'Opérations',
    icon: FolderOpen,
    color: 'text-blue-600',
    items: [
      { id: 'overview', label: "Vue d'ensemble", icon: BarChart3 },
      { id: 'projects', label: 'Dossiers', icon: FolderOpen },
      { id: 'expert', label: 'Dashboard Expert', icon: Calculator },
      { id: 'commerciaux', label: 'Mes Commerciaux', icon: Users },
    ]
  },
  {
    id: 'prospects',
    label: 'Prospects',
    icon: TrendingUp,
    color: 'text-purple-600',
    items: [
      { id: 'prospects_all', label: 'Tous les Prospects', icon: TrendingUp },
      { id: 'prospects_estimation', label: 'Prospects Estimation', icon: Calculator },
      { id: 'prospects_radar', label: 'Prospects Radar Foncier', icon: Navigation },
      { id: 'prospects_audit', label: 'Prospects Audit', icon: FileText },
      { id: 'prospects_conseil', label: 'Prospects Conseil / Travaux', icon: Shield },
    ]
  },
  {
    id: 'notes_group',
    label: 'Notes',
    icon: StickyNote,
    color: 'text-yellow-600',
    items: [
      { id: 'notes', label: 'Mes notes', icon: StickyNote },
    ]
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: Headphones,
    color: 'text-orange-600',
    items: [
      { id: 'hotline', label: 'Hotline', icon: Headphones },
    ]
  },
  {
    id: 'vitrine_group',
    label: 'Vitrine de biens',
    icon: Home,
    color: 'text-emerald-600',
    items: [
      { id: 'vitrine', label: 'Mes annonces', icon: Home },
    ]
  },
  {
    id: 'config',
    label: 'Configuration',
    icon: Settings,
    color: 'text-gray-600',
    items: [
      { id: 'profile', label: 'Mon Profil', icon: UserCheck },
      { id: 'legal', label: 'Infos Légales', icon: FileText },
      { id: 'branding', label: 'Personnalisation', icon: Palette },
      { id: 'kit_pub', label: 'Kit Communication', icon: QrCode },
      { id: 'emails', label: 'Emails Clients', icon: Mail },
      { id: 'estimation_coefs', label: 'Coefficients Estimation', icon: Settings },
      { id: 'purge', label: 'Purge données', icon: Trash2 },
    ]
  },
];

function PartnerSidebar({ activeTab, onTabChange, badges = {}, mobileOpen = false, onMobileClose = () => {}, domainEnabled = false, isInternational = false }) {
  const navigate = useNavigate();
  
  // Onglets masqués selon le type de partenaire
  const HIDDEN_ITEMS = isInternational
    ? ['prospects_radar', 'prospects_audit', 'prospects_conseil'] // Masqués pour les étrangers
    : ['vitrine']; // Vitrine masquée pour les partenaires français

  const effectiveCategories = NAV_CATEGORIES.map(cat => {
    let items = [...cat.items];
    items = items.filter(i => !HIDDEN_ITEMS.includes(i.id));
    if (cat.id !== 'config') return { ...cat, items };
    const profileIdx = items.findIndex(i => i.id === 'profile');
    if (domainEnabled && !items.find(i => i.id === 'domaine')) {
      items.splice(profileIdx + 1, 0, { id: 'domaine', label: 'Mon Domaine', icon: Globe });
    }
    return { ...cat, items };
  }).filter(cat => cat.items.length > 0);

  const [expanded, setExpanded] = useState(() => {
    const init = {};
    effectiveCategories.forEach(cat => {
      if (cat.items.some(i => i.id === activeTab)) init[cat.id] = true;
    });
    if (Object.keys(init).length === 0) init['operations'] = true;
    return init;
  });

  // Auto-ouvrir CONFIGURATION quand Mon Domaine est disponible
  useEffect(() => {
    if (domainEnabled) {
      setExpanded(prev => ({ ...prev, config: true }));
    }
  }, [domainEnabled]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleItemClick = (itemId) => {
    if (itemId === 'expert') {
      navigate('/partenaire/expert');
      return;
    }
    onTabChange(itemId);
    onMobileClose();
  };

  const filteredCategories = searchQuery.trim()
    ? effectiveCategories.map(cat => ({
        ...cat,
        items: cat.items.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
      })).filter(cat => cat.items.length > 0)
    : effectiveCategories;

  return (
    <>
      <aside className={`
        lg:w-56 lg:shrink-0 lg:relative lg:block
        ${mobileOpen ? 'block w-full' : 'hidden lg:block'}
      `} data-testid="partner-sidebar">
        <div className={`${mobileOpen ? 'h-full overflow-y-auto' : 'sticky top-6'} bg-white rounded-xl shadow-sm border overflow-hidden`}>
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs bg-gray-50 border-gray-200 pr-7" data-testid="partner-sidebar-search" />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <nav className="p-2 max-h-[calc(100vh-200px)] overflow-y-auto">
            {filteredCategories.map((category) => {
              const isExpanded = searchQuery.trim() ? true : expanded[category.id];
              const CatIcon = category.icon;
              const hasActive = category.items.some(i => i.id === activeTab);
              return (
                <div key={category.id} className="mb-1">
                  <button onClick={() => setExpanded(p => ({ ...p, [category.id]: !p[category.id] }))}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors ${
                      hasActive ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'
                    }`} data-testid={`partner-nav-${category.id}`}>
                    <span className="flex items-center gap-2"><CatIcon className={`w-3.5 h-3.5 ${category.color}`} />{category.label}</span>
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  {isExpanded && (
                    <div className="mt-0.5 ml-2 space-y-0.5">
                      {category.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeTab === item.id;
                        const badge = badges[item.id];
                        return (
                          <button key={item.id} onClick={() => handleItemClick(item.id)}
                            className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-all ${
                              isActive ? 'bg-[#0f1d3c] text-white font-medium shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                            }`} data-testid={`partner-nav-item-${item.id}`}>
                            <span className="flex items-center gap-2">
                              <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-amber-400' : ''}`} />{item.label}
                            </span>
                            {badge > 0 && (
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-red-500 text-white' : 'bg-red-100 text-red-600'}`}>{badge}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
}


// Composant éditeur de subdomain
function SubdomainEditor({ currentSubdomain, onSaved, getHeaders }) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(currentSubdomain);
  const [saving, setSaving] = React.useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => { setValue(currentSubdomain); }, [currentSubdomain]);

  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/partenaire/settings/subdomain`, { subdomain: value.trim() }, { headers: getHeaders() });
      toast.success(res.data.message || 'URL mise à jour !');
      onSaved(value.trim().toLowerCase());
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium text-purple-600">
          /p/{currentSubdomain || <span className="text-gray-400 italic">non défini</span>}
        </span>
        <button onClick={() => setEditing(true)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          ✏️ {currentSubdomain ? 'Modifier' : 'Définir'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <span className="text-sm text-gray-500 font-mono">/p/</span>
        <input
          type="text"
          value={value}
          onChange={e => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, ''))}
          placeholder="mon-agence"
          className="border rounded px-2 py-1 text-sm font-mono w-36 focus:ring-2 focus:ring-purple-400"
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          autoFocus
        />
      </div>
      <p className="text-xs text-gray-400">Lettres minuscules, chiffres, tirets uniquement</p>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving || !value.trim()} className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50">
          {saving ? 'Enregistrement...' : 'Valider'}
        </button>
        <button onClick={() => { setEditing(false); setValue(currentSubdomain); }} className="px-3 py-1 bg-gray-200 text-gray-600 rounded text-xs hover:bg-gray-300">
          Annuler
        </button>
      </div>
    </div>
  );
}


// ───── Composant Mes Commerciaux ─────
function CommerciauxTab({ getHeaders, primaryColor, partnerSubdomain }) {
  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', fonction: 'Commercial', telephone: '' });
  const [creating, setCreating] = useState(false);
  const [newCred, setNewCred] = useState(null); // { full_name, email, temp_password, public_url }

  const fetchCommerciaux = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/partenaire/commerciaux`, { headers: getHeaders() });
      setCommerciaux(res.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCommerciaux(); }, []);

  const createCommercial = async () => {
    if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) {
      toast.error('Nom, prénom et email requis'); return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/partenaire/commerciaux`, form, { headers: getHeaders() });
      toast.success('Commercial créé ! Email envoyé.');
      setNewCred(res.data);
      setForm({ nom: '', prenom: '', email: '', fonction: 'Commercial', telephone: '' });
      setShowForm(false);
      fetchCommerciaux();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la création'); }
    finally { setCreating(false); }
  };

  const deleteCommercial = async (id) => {
    if (!window.confirm('Supprimer ce commercial ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API_URL}/partenaire/commerciaux/${id}`, { headers: getHeaders() });
      toast.success('Commercial supprimé');
      setCommerciaux(c => c.filter(x => x.id !== id));
    } catch { toast.error('Erreur suppression'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Mes Commerciaux</h2>
        <Button onClick={() => setShowForm(true)} className="text-white" style={{ backgroundColor: primaryColor }}>
          <Plus className="w-4 h-4 mr-2" /> Nouveau commercial
        </Button>
      </div>

      {/* Formulaire création */}
      {showForm && (
        <Card className="border-2" style={{ borderColor: primaryColor + '40' }}>
          <CardHeader><CardTitle className="text-base">Créer un commercial</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-gray-700">Prénom *</label>
                <Input value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} className="mt-1" placeholder="Jean" data-testid="commercial-form-prenom" /></div>
              <div><label className="text-sm font-medium text-gray-700">Nom *</label>
                <Input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} className="mt-1" placeholder="Dupont" data-testid="commercial-form-nom" /></div>
            </div>
            <div><label className="text-sm font-medium text-gray-700">Email *</label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1" placeholder="jean.dupont@agence.com" data-testid="commercial-form-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-gray-700">Fonction</label>
                <Input value={form.fonction} onChange={e => setForm(f => ({ ...f, fonction: e.target.value }))} className="mt-1" placeholder="Commercial" /></div>
              <div><label className="text-sm font-medium text-gray-700">Téléphone</label>
                <Input value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} className="mt-1" placeholder="06 12 34 56 78" /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={createCommercial} disabled={creating} className="text-white" style={{ backgroundColor: primaryColor }} data-testid="commercial-form-submit">
                {creating ? 'Création...' : 'Créer le commercial'}
              </Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setForm({ nom: '', prenom: '', email: '', fonction: 'Commercial', telephone: '' }); }}>
                Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Affichage des identifiants créés */}
      {newCred && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-emerald-800">Commercial créé avec succès</p>
                <p className="text-sm text-emerald-700 mt-1">Un email a été envoyé à <strong>{newCred.email}</strong></p>
                <div className="mt-3 space-y-1 text-sm">
                  <p><span className="text-gray-600">Nom :</span> <strong>{newCred.full_name}</strong></p>
                  <p><span className="text-gray-600">Mot de passe temporaire :</span> <code className="bg-white px-2 py-0.5 rounded border border-emerald-200 font-mono">{newCred.temp_password}</code></p>
                  <p><span className="text-gray-600">Page publique :</span> <a href={newCred.public_url} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">{newCred.public_url}</a></p>
                  <p><span className="text-gray-600">Connexion :</span> <a href="/commercial/login" className="text-purple-600 hover:underline">{window.location.origin}/commercial/login</a></p>
                </div>
              </div>
              <button onClick={() => setNewCred(null)} className="text-emerald-400 hover:text-emerald-600"><X className="w-4 h-4" /></button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des commerciaux */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Chargement...</div>
      ) : commerciaux.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Aucun commercial pour l'instant</p>
          <p className="text-sm mt-1">Créez votre premier commercial pour lui générer une page publique et des accès.</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {commerciaux.map(c => (
            <Card key={c.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                      style={{ backgroundColor: primaryColor }}>
                      {(c.prenom?.[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{c.full_name}</p>
                      <p className="text-sm text-gray-500">{c.fonction} · {c.email}</p>
                      {c.telephone && <p className="text-xs text-gray-400">{c.telephone}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ color: primaryColor }}>{c.prospects_count || 0}</p>
                      <p className="text-xs text-gray-400">prospects</p>
                    </div>
                    {partnerSubdomain && c.slug && (
                      <a href={`/p/${partnerSubdomain}/${c.slug}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs flex items-center gap-1 text-purple-600 hover:underline border border-purple-200 px-2 py-1 rounded-lg">
                        <ExternalLink className="w-3 h-3" /> Page
                      </a>
                    )}
                    <button
                      title="Réinitialiser le mot de passe"
                      onClick={async () => {
                        if (!window.confirm(`Réinitialiser le mot de passe de ${c.full_name} ? Un nouveau mot de passe sera envoyé par email.`)) return;
                        try {
                          const res = await axios.put(`${API_URL}/partenaire/commerciaux/${c.id}/reset-password`, {}, { headers: getHeaders() });
                          toast.success(`Nouveau mot de passe : ${res.data.new_password} (envoyé par email)`);
                        } catch { toast.error('Erreur lors de la réinitialisation'); }
                      }}
                      className="text-gray-300 hover:text-amber-500 transition-colors p-1">
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteCommercial(c.id)}
                      className="text-gray-300 hover:text-red-500 transition-colors p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ───── Composant Mon Domaine ─────
function PartnerDomainTab({ getHeaders }) {
  const [name, setName] = useState('');
  const [results, setResults] = useState([]);
  const [checking, setChecking] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    axios.get(`${API_URL}/partenaire/domain/request`, { headers: getHeaders() })
      .then(res => { if (res.data && res.data.domain) setExisting(res.data); })
      .catch(() => {})
      .finally(() => setLoadingExisting(false));
  }, []);

  const checkDomains = async () => {
    const n = name.trim().toLowerCase().replace(/\s+/g, '-');
    if (!n) return;
    setChecking(true); setResults([]); setSelected(null);
    try {
      const res = await axios.get(`${API_URL}/domain/check?name=${encodeURIComponent(n)}`);
      setResults(res.data || []);
    } catch { toast.error('Erreur lors de la vérification'); }
    finally { setChecking(false); }
  };

  const submitRequest = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/partenaire/domain/request`, {
        domain: selected.domain, price_ttc: selected.price_ttc, currency: selected.currency, tld: selected.tld
      }, { headers: getHeaders() });
      toast.success('Demande envoyée ! Vous recevrez un email de confirmation.');
      setExisting({ domain: selected.domain, status: 'pending', price_ttc: selected.price_ttc });
      setResults([]); setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const STATUS_LABELS = {
    pending: { label: 'En attente — votre demande est en cours de traitement', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: Clock },
    bought:  { label: 'Domaine acheté — configuration DNS en cours', color: 'text-blue-700 bg-blue-50 border-blue-200', icon: CheckCircle },
    active:  { label: 'Actif — votre domaine est opérationnel', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: CheckCircle },
  };

  if (loadingExisting) return <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="partner-domain-tab">
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Globe className="w-5 h-5 text-blue-600" /> Mon Domaine Personnalisé</h2>
        <p className="text-sm text-gray-500 mt-1">Obtenez un domaine propre pour votre page (ex : <em>monagence.fr</em>).</p>
      </div>

      {/* Demande existante */}
      {existing && (
        <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${STATUS_LABELS[existing.status]?.color || 'bg-gray-50 border-gray-200 text-gray-700'}`}>
          {React.createElement(STATUS_LABELS[existing.status]?.icon || Clock, { className: 'w-5 h-5 mt-0.5 shrink-0' })}
          <div>
            <p className="font-semibold">{existing.domain}</p>
            <p className="text-sm mt-0.5">{STATUS_LABELS[existing.status]?.label || existing.status}</p>
          </div>
        </div>
      )}

      {/* Formulaire de recherche */}
      {!existing && (
        <>
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <label className="block text-sm font-semibold text-gray-700">Rechercher un nom de domaine</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, ''))}
                onKeyDown={e => e.key === 'Enter' && checkDomains()}
                placeholder="ex: mon-agence"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none"
                data-testid="domain-search-input"
              />
              <button
                onClick={checkDomains}
                disabled={checking || !name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                data-testid="domain-check-btn"
              >
                {checking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {checking ? 'Vérification...' : 'Vérifier'}
              </button>
            </div>
            <p className="text-xs text-gray-400">Lettres minuscules, chiffres et tirets uniquement. Extensions vérifiées : .com, .fr, .net</p>
          </div>

          {/* Résultats */}
          {results.length > 0 && (
            <div className="space-y-2">
              {results.map(r => (
                <button
                  key={r.domain}
                  onClick={() => r.available && setSelected(r)}
                  disabled={!r.available}
                  data-testid={`domain-result-${r.tld}`}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-all ${
                    !r.available
                      ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                      : selected?.domain === r.domain
                      ? 'bg-blue-50 border-blue-500 text-blue-900 ring-2 ring-blue-400'
                      : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-800'
                  }`}
                >
                  <span className="font-semibold">{r.domain}</span>
                  <span className="flex items-center gap-2">
                    {r.available ? (
                      <>
                        <span className="text-emerald-600 font-medium text-xs bg-emerald-50 px-2 py-0.5 rounded-full">Disponible</span>
                        {r.price_ttc && <span className="font-bold">{r.price_ttc} {r.currency}/an</span>}
                      </>
                    ) : (
                      <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Pris</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Confirmation */}
          {selected && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-blue-900">
                Vous avez sélectionné : <strong>{selected.domain}</strong>
                {selected.price_ttc && <span className="ml-2 text-blue-600">— {selected.price_ttc} {selected.currency}/an</span>}
              </p>
              <p className="text-xs text-blue-700">Votre demande sera envoyée à l'administrateur qui achètera le domaine et configurera votre page. Vous recevrez un email de confirmation à chaque étape.</p>
              <button
                onClick={submitRequest}
                disabled={submitting}
                data-testid="domain-submit-btn"
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              >
                {submitting ? <><RefreshCw className="w-4 h-4 animate-spin" /> Envoi...</> : <><Globe className="w-4 h-4" /> Demander ce domaine</>}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function PartenaireDashboard() {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // Data states
  const [stats, setStats] = useState({});
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [radarProspects, setRadarProspects] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [conseils, setConseils] = useState([]);
  const [notes, setNotes] = useState([]);
  const [accounting, setAccounting] = useState({ invoices: [], total_ht: 0, total_tva: 0, total_ttc: 0, total_paid: 0, total_pending: 0, monthly_breakdown: {} });
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({});

  // UI states
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [commProject, setCommProject] = useState(null);  // dossier sélectionné pour la modal Communication
  const [prospectSearch, setProspectSearch] = useState('');
  const [stripeKey, setStripeKey] = useState('');
  const [savingStripe, setSavingStripe] = useState(false);
  // Algérie — paiement manuel DZD
  const [algeriaPaymentForm, setAlgeriaPaymentForm] = useState({ payment_type: 'virement', amount_dzd: 15000, reference: '', note: '' });
  const [sendingAlgeriaPayment, setSendingAlgeriaPayment] = useState(false);
  // Vitrine de biens
  const [annonces, setAnnonces] = useState([]);
  const [annonceFormOpen, setAnnonceFormOpen] = useState(false);
  const [editingAnnonce, setEditingAnnonce] = useState(null);
  const [algeriaPriceGrid, setAlgeriaPriceGrid] = useState(null);
  const [annonceForm, setAnnonceForm] = useState({
    transaction_type: 'vente', property_type: 'appartement',
    zone: '', zone_label: '', city: '', price: '', surface: '', rooms: '',
    title: '', description: '', contact_phone: '', status: 'active',
  });
  const [savingAnnonce, setSavingAnnonce] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [purgePreview, setPurgePreview] = useState([]);
  const [purgingKey, setPurgingKey] = useState(null);
  // Vue coefficients (partner-specific)
  const [vueCoefs, setVueCoefs] = useState([]);
  const [editingVueCat, setEditingVueCat] = useState(null); // { catId, subs }
  const [savingVue, setSavingVue] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#7C3AED');
  const [selectedAccentColor, setSelectedAccentColor] = useState('#F59E0B');
  const [selectedCtaColor, setSelectedCtaColor] = useState('#10B981');
  const [selectedHeaderColor, setSelectedHeaderColor] = useState('');
  const [selectedTextColor, setSelectedTextColor] = useState('');
  const [savingBranding, setSavingBranding] = useState(false);
  const [emailConfirmationText, setEmailConfirmationText] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [exampleLang, setExampleLang] = useState('fr'); // Langue pour "Exemple immobilier"
  // Nouveaux champs luxe
  const [slogan, setSlogan] = useState('');
  const [aboutText, setAboutText] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [fontChoice, setFontChoice] = useState('inter');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [aboutImageUrl, setAboutImageUrl] = useState('');
  const [galleryImageUrl, setGalleryImageUrl] = useState('');
  const [whyUs, setWhyUs] = useState([
    { title: '', text: '', icon_name: 'award' },
    { title: '', text: '', icon_name: 'zap' },
    { title: '', text: '', icon_name: 'shield' },
  ]);
  const [customStats, setCustomStats] = useState([
    { value: '', label: '' }, { value: '', label: '' },
    { value: '', label: '' }, { value: '', label: '' },
  ]);
  const logoInputRef = useRef(null);
  const heroImageInputRef = useRef(null);
  const aboutImageInputRef = useRef(null);
  const galleryImageInputRef = useRef(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingAbout, setUploadingAbout] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '', client_email: '' });
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [legalForm, setLegalForm] = useState({ company_name: '', siret: '', tva_number: '', bank_iban: '', bank_bic: '', address: '', city_postal: '', legal_form: '', capital_social: '', rcs_info: '' });
  const [savingLegal, setSavingLegal] = useState(false);
  const [exportYear, setExportYear] = useState('all');
  const [exportMonth, setExportMonth] = useState('all');
  const [exporting, setExporting] = useState(null);
  const [profileForm, setProfileForm] = useState({ nom: '', prenom: '', telephone: '', company_name: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [subscribing, setSubscribing] = useState(false);

  // Pagination hooks (10 éléments par page, plus récents en premier)
  const projectsPagination = usePagination(10);
  const estimationPagination = usePagination(10);
  const radarPagination = usePagination(10);
  const auditPagination = usePagination(10);
  const conseilPropagination = usePagination(10);
  const chantiersPagination = usePagination(10);
  const conseilsDemandsPagination = usePagination(10);

  const brandColors = [
    { name: 'Violet', value: '#7C3AED' },{ name: 'Bleu', value: '#3B82F6' },
    { name: 'Vert', value: '#10B981' },{ name: 'Orange', value: '#F59E0B' },
    { name: 'Rouge', value: '#EF4444' },{ name: 'Rose', value: '#EC4899' },
    { name: 'Cyan', value: '#06B6D4' },{ name: 'Marine', value: '#1e3a5f' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    const partnerData = localStorage.getItem('partner_data');
    if (!token || !partnerData) { navigate('/partenaire/login'); return; }
    try {
      setPartner(JSON.parse(partnerData));
      fetchAllData(token);
    } catch { navigate('/partenaire/login'); }
  }, [navigate]);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('partner_token')}` });

  const fetchAllData = async (token) => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, clientsRes, projectsRes, prospectsRes, radarRes, chantiersRes, conseilsRes, notesRes, accountingRes, messagesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/partenaire/stats/extended`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/partenaire/clients`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partenaire/projects`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partenaire/prospects`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partenaire/prospects/radar`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partenaire/chantiers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partenaire/conseil-demands`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partenaire/notes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partenaire/accounting`, { headers }).catch(() => ({ data: { invoices: [], total_ht: 0, total_tva: 0, total_ttc: 0, total_paid: 0, total_pending: 0, monthly_breakdown: {} } })),
        axios.get(`${API}/partenaire/hotline/messages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partenaire/settings`, { headers }).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data); setClients(clientsRes.data); setProjects(projectsRes.data);
      setProspects(prospectsRes.data); setRadarProspects(radarRes.data); setChantiers(chantiersRes.data); setConseils(conseilsRes.data);
      setNotes(notesRes.data); setAccounting(accountingRes.data); setMessages(messagesRes.data);
      setSettings(settingsRes.data); setSelectedColor(settingsRes.data.primary_color || '#7C3AED');
      
      // Rafraîchir l'identité partenaire depuis l'API (évite les données périmées du localStorage)
      if (settingsRes.data.email) {
        setPartner(prev => ({
          ...prev,
          full_name: settingsRes.data.full_name || prev.full_name,
          company_name: settingsRes.data.company_name || prev.company_name,
          // Toujours prendre le subdomain de l'API (même si vide) — évite le subdomain d'une session précédente
          subdomain: settingsRes.data.subdomain !== undefined ? settingsRes.data.subdomain : prev.subdomain,
          email: settingsRes.data.email || prev.email,
          domain_enabled: settingsRes.data.domain_enabled || false,
        }));
        // Mettre à jour localStorage aussi
        const storedPartner = JSON.parse(localStorage.getItem('partner_data') || '{}');
        localStorage.setItem('partner_data', JSON.stringify({
          ...storedPartner,
          full_name: settingsRes.data.full_name || storedPartner.full_name,
          company_name: settingsRes.data.company_name || storedPartner.company_name,
          subdomain: settingsRes.data.subdomain !== undefined ? settingsRes.data.subdomain : storedPartner.subdomain,
          domain_enabled: settingsRes.data.domain_enabled || false,
        }));
      }
      setSelectedAccentColor(settingsRes.data.secondary_color || '#F59E0B');
      setSelectedCtaColor(settingsRes.data.cta_color || '#10B981');
      setSelectedHeaderColor(settingsRes.data.header_color || '');
      setSelectedTextColor(settingsRes.data.text_color || '');
      setLogoPreview(settingsRes.data.logo_url || null);

      // Load Vue coefficients
      axios.get(`${API}/partenaire/vue-coefficients`, { headers: getHeaders() })
        .then(res => {
          if (res.data && res.data.length) {
            setVueCoefs(res.data.map(cat => ({
              id: cat.id, category_id: cat.category_id, label: cat.category_label || cat.label,
              icon: cat.category_icon || cat.icon, subtitle: cat.category_subtitle || cat.subtitle,
              subs: (cat.subs || cat.sub || []).map(s => ({ sub_id: s.sub_id || s.id, sub_label: s.sub_label || s.label, coef: s.coef }))
            })));
          }
        })
        .catch(() => {});
      setSlogan(settingsRes.data.slogan || '');
      setAboutText(settingsRes.data.about_text || '');
      setContactPhone(settingsRes.data.contact_phone || '');
      setContactEmail(settingsRes.data.contact_email || '');
      setWebsiteUrl(settingsRes.data.website_url || '');
      setHeroImageUrl(settingsRes.data.hero_image_url || '');
      setAboutImageUrl(settingsRes.data.about_image_url || '');
      setGalleryImageUrl(settingsRes.data.gallery_image_url || '');
      setFontChoice(settingsRes.data.font_choice || 'inter');
      setEmailConfirmationText(settingsRes.data.email_confirmation_text || '');
      setSlogan(settingsRes.data.slogan || '');
      setAboutText(settingsRes.data.about_text || '');
      setContactPhone(settingsRes.data.contact_phone || '');
      setContactEmail(settingsRes.data.contact_email || '');
      setWebsiteUrl(settingsRes.data.website_url || '');
      setFontChoice(settingsRes.data.font_choice || 'inter');
      setHeroImageUrl(settingsRes.data.hero_image_url || '');
      if (settingsRes.data.why_us?.length > 0) setWhyUs(settingsRes.data.why_us);
      if (settingsRes.data.custom_stats?.length > 0) setCustomStats(settingsRes.data.custom_stats);

      // Charger annonces vitrine + grille tarifaire
      try {
        const [annRes, gridRes] = await Promise.all([
          axios.get(`${API}/partenaire/annonces`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/algerie/price-grid`).catch(() => ({ data: null })),
        ]);
        setAnnonces(annRes.data || []);
        setAlgeriaPriceGrid(gridRes.data);
      } catch (e) { console.log('Vitrine load error', e); }
      // Pré-remplir le formulaire légal
      const s = settingsRes.data;
      setLegalForm({
        company_name: s.company_name || '',
        siret: s.siret || '',
        tva_number: s.tva_number || '',
        bank_iban: s.bank_iban || '',
        bank_bic: s.bank_bic || '',
        address: s.address || '',
        city_postal: s.city_postal || '',
        legal_form: s.legal_form || '',
        capital_social: s.capital_social || '',
        rcs_info: s.rcs_info || '',
      });
      setProfileForm({
        nom: s.nom || '',
        prenom: s.prenom || '',
        telephone: s.phone || '',
        company_name: s.company_name || '',
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleLogout = () => {
    const sub = partner?.subdomain || '';
    localStorage.removeItem('partner_token');
    localStorage.removeItem('partner_data');
    clearPartner();
    navigate(`/partenaire/login${sub ? '?sub=' + sub : ''}`);
    toast.success('Déconnexion réussie');
  };

  const sendHotlineMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await axios.post(`${API}/partenaire/hotline/send`, { message: newMessage }, { headers: getHeaders() });
      toast.success('Message envoyé');
      setNewMessage('');
      const res = await axios.get(`${API}/partenaire/hotline/messages`, { headers: getHeaders() });
      setMessages(res.data);
    } catch { toast.error("Erreur lors de l'envoi"); }
    finally { setSendingMessage(false); }
  };

  const saveStripeKey = async () => {
    if (!stripeKey.trim()) { toast.error('Entrez votre clé Stripe'); return; }
    setSavingStripe(true);
    try {
      await axios.post(`${API}/partenaire/settings/stripe`, { stripe_api_key: stripeKey }, { headers: getHeaders() });
      toast.success('Clé Stripe configurée !'); setStripeKey('');
      const res = await axios.get(`${API}/partenaire/settings`, { headers: getHeaders() });
      setSettings(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSavingStripe(false); }
  };

  const removeStripeKey = async () => {
    if (!window.confirm('Supprimer votre clé Stripe ?')) return;
    try {
      await axios.delete(`${API}/partenaire/settings/stripe`, { headers: getHeaders() });
      toast.success('Clé Stripe supprimée');
      setSettings(s => ({ ...s, stripe_configured: false, stripe_key_masked: '' }));
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteProject = async (project) => {
    if (!window.confirm(`Supprimer définitivement le dossier "${project.address}" ?\n\nCette action est irréversible.`)) return;
    setDeletingProjectId(project.id);
    try {
      await axios.delete(`${API}/partenaire/projects/${project.id}`, { headers: getHeaders() });
      setProjects(prev => prev.filter(p => p.id !== project.id));
      toast.success('Dossier supprimé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingProjectId(null);
    }
  };

  const handlePurgeProjects = async () => {
    if (purgeConfirmText !== 'SUPPRIMER TOUT') {
      toast.error('Tapez exactement "SUPPRIMER TOUT" pour confirmer');
      return;
    }
    setPurging(true);
    try {
      const res = await axios.post(`${API}/partenaire/purge/all`, {}, { headers: getHeaders() });
      // Vider tout l'état frontend après purge complète
      setProjects([]); setProspects([]); setRadarProspects([]);
      setConseils([]); setClients([]); setChantiers([]);
      setStats(s => ({ ...s, total_prospects: 0, total_projects: 0, total_radar: 0, prospects_estimation: 0, prospects_conseil: 0, total_conseil: 0, total_chantiers: 0, total_clients: 0 }));
      setPurgeConfirmText('');
      setPurgePreview([]);
      toast.success(`${res.data.total} élément(s) supprimé(s)`);
      await loadPurgePreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setPurging(false);
    }
  };

  const loadPurgePreview = async () => {
    try {
      const res = await axios.get(`${API}/partenaire/purge/preview`, { headers: getHeaders() });
      setPurgePreview(res.data.collections || []);
    } catch {}
  };

  const purgeOneCollection = async (key) => {
    setPurgingKey(key);
    try {
      const res = await axios.post(`${API}/partenaire/purge/collection/${key}`, {}, { headers: getHeaders() });
      toast.success(`${res.data.deleted} élément(s) supprimé(s)`);
      // Vider le state React correspondant à la collection purgée
      if (key === 'projects') setProjects([]);
      if (key === 'prospects') { setProspects([]); setRadarProspects([]); }
      if (key === 'prospection_clients') setRadarProspects([]);
      if (key === 'conseil_demands') setConseils([]);
      if (key === 'chantier_demands') setChantiers([]);
      if (key === 'users') setClients([]);
      setStats(s => ({
        ...s,
        ...(key === 'projects' ? { total_projects: 0, pending_projects: 0 } : {}),
        ...(key === 'prospects' ? { total_prospects: 0, prospects_estimation: 0, prospects_conseil: 0, total_radar: 0 } : {}),
        ...(key === 'prospection_clients' ? { total_radar: 0 } : {}),
        ...(key === 'conseil_demands' ? { total_conseil: 0, prospects_conseil: 0 } : {}),
        ...(key === 'chantier_demands' ? { total_chantiers: 0 } : {}),
        ...(key === 'users' ? { total_clients: 0 } : {}),
      }));
      await loadPurgePreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setPurgingKey(null);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 5MB)'); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const saveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      await axios.put(`${API}/partenaire/settings/email`, { email_confirmation_text: emailConfirmationText }, { headers: getHeaders() });
      toast.success('Texte email enregistré !');
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingEmail(false);
    }
  };

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      let logoUrl = settings.logo_url;
      if (logoFile) {
        setUploadingLogo(true);
        const fd = new FormData(); fd.append('file', logoFile);
        const uploadRes = await axios.post(`${API}/upload/logo`, fd);
        logoUrl = uploadRes.data.logo_url; setUploadingLogo(false);
      }
      await axios.put(`${API}/partenaire/settings/branding`, {
        logo_url: logoUrl, primary_color: selectedColor,
        secondary_color: selectedAccentColor,
        cta_color: selectedCtaColor,
        header_color: selectedHeaderColor || undefined,
        text_color: selectedTextColor,
        slogan, about_text: aboutText, contact_phone: contactPhone, contact_email: contactEmail,
        website_url: websiteUrl,
        font_choice: fontChoice, hero_image_url: heroImageUrl,
        about_image_url: aboutImageUrl,
        gallery_image_url: galleryImageUrl,
        why_us: whyUs.filter(w => w.title),
        custom_stats: customStats.filter(s => s.value && s.label),
      }, { headers: getHeaders() });
      toast.success('Personnalisation enregistrée !'); setLogoFile(null);
      const res = await axios.get(`${API}/partenaire/settings`, { headers: getHeaders() });
      setSettings(res.data); setLogoPreview(res.data.logo_url || null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSavingBranding(false); setUploadingLogo(false); }
  };

  const handleHeroImageUpload = async (file) => {
    if (!file) return;
    const allowed = ['jpg', 'jpeg', 'png', 'webp'];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) { toast.error('Format non supporté. Utilisez JPG, PNG ou WebP'); return; }
    setUploadingHero(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/upload/hero-image`, fd);
      setHeroImageUrl(res.data.hero_image_url);
      toast.success('Image bannière chargée !');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload image'); }
    finally { setUploadingHero(false); }
  };

  const handleAboutImageUpload = async (file) => {
    if (!file) return;
    setUploadingAbout(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/upload/hero-image`, fd);
      setAboutImageUrl(res.data.hero_image_url);
      toast.success('Image "À propos" chargée !');
    } catch (err) { toast.error('Erreur upload image'); }
    finally { setUploadingAbout(false); }
  };

  const handleGalleryImageUpload = async (file) => {
    if (!file) return;
    setUploadingGallery(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/upload/hero-image`, fd);
      setGalleryImageUrl(res.data.hero_image_url);
      toast.success('Image galerie chargée !');
    } catch (err) { toast.error('Erreur upload image'); }
    finally { setUploadingGallery(false); }
  };

  const DEFAULT_HERO_IMG = 'https://images.unsplash.com/photo-1505843795480-5cfb3c03f6ff?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85';
  const DEFAULT_ABOUT_IMG = 'https://images.pexels.com/photos/8730006/pexels-photo-8730006.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
  const DEFAULT_GALLERY_IMG = 'https://images.pexels.com/photos/18153132/pexels-photo-18153132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  const fillImmobilierDefaults = (lang = exampleLang) => {
    const examples = {
      fr: {
        slogan: "L'immobilier, une affaire de confiance",
        about: "Votre partenaire de confiance pour tous vos projets immobiliers. Notre équipe d'experts vous accompagne à chaque étape : estimation, diagnostics, négociation et suivi. Nous mettons notre connaissance du marché local au service de votre réussite.",
        stats: [
          { value: '350+', label: 'Biens vendus' },
          { value: '15', label: "Ans d'expérience" },
          { value: '98%', label: 'Clients satisfaits' },
          { value: '12', label: 'Experts dédiés' },
        ],
        whyUs: [
          { title: 'Expertise locale', text: 'Une connaissance approfondie du marché immobilier de votre région pour estimer au juste prix.', icon_name: 'map-pin' },
          { title: 'Accompagnement complet', text: "De l'estimation à la signature chez le notaire, nous gérons chaque étape pour vous.", icon_name: 'shield' },
          { title: 'Transparence totale', text: 'Rapports détaillés, diagnostics fiables et conseils honnêtes pour décider en toute sérénité.', icon_name: 'award' },
        ],
      },
      ar: {
        slogan: 'العقار، شأن من شؤون الثقة',
        about: 'شريكك الموثوق في جميع مشاريعك العقارية. يرافقك فريقنا من الخبراء في كل خطوة: التقدير، المعاينة، التفاوض والمتابعة. نضع معرفتنا بالسوق المحلية في خدمة نجاحك.',
        stats: [
          { value: '350+', label: 'عقار مباع' },
          { value: '15', label: 'سنة خبرة' },
          { value: '98%', label: 'عملاء راضون' },
          { value: '12', label: 'خبير مخصص' },
        ],
        whyUs: [
          { title: 'خبرة محلية', text: 'معرفة عميقة بسوق العقارات في منطقتك لتقدير السعر العادل.', icon_name: 'map-pin' },
          { title: 'مرافقة كاملة', text: 'من التقدير إلى توقيع العقد، نتولى كل خطوة نيابة عنك.', icon_name: 'shield' },
          { title: 'شفافية تامة', text: 'تقارير مفصلة ونصائح صادقة لتتخذ قراراتك بكل اطمئنان.', icon_name: 'award' },
        ],
      },
      en: {
        slogan: 'Real estate is a matter of trust',
        about: 'Your trusted partner for all your real estate projects. Our team of experts guides you at every step: valuation, surveys, negotiation and follow-up. We put our local market knowledge at the service of your success.',
        stats: [
          { value: '350+', label: 'Properties sold' },
          { value: '15', label: 'Years of experience' },
          { value: '98%', label: 'Satisfied clients' },
          { value: '12', label: 'Dedicated experts' },
        ],
        whyUs: [
          { title: 'Local expertise', text: 'An in-depth knowledge of the real estate market in your area to estimate the right price.', icon_name: 'map-pin' },
          { title: 'Full support', text: 'From valuation to signing at the notary, we handle every step for you.', icon_name: 'shield' },
          { title: 'Total transparency', text: 'Detailed reports, reliable surveys and honest advice to make decisions with peace of mind.', icon_name: 'award' },
        ],
      },
    };
    const ex = examples[lang] || examples['fr'];
    if (!slogan) setSlogan(ex.slogan);
    if (!aboutText) setAboutText(ex.about);
    if (!contactPhone) setContactPhone('01 23 45 67 89');
    if (!websiteUrl) setWebsiteUrl('https://www.monagenceimmo.fr');
    if (!heroImageUrl) setHeroImageUrl(DEFAULT_HERO_IMG);
    if (!aboutImageUrl) setAboutImageUrl(DEFAULT_ABOUT_IMG);
    if (!galleryImageUrl) setGalleryImageUrl(DEFAULT_GALLERY_IMG);
    if (customStats.every(s => !s.value)) setCustomStats(ex.stats);
    if (whyUs.every(w => !w.title)) setWhyUs(ex.whyUs);
    toast.success('Exemple chargé — pensez à Enregistrer !');
  };

  const saveLegal = async () => {
    setSavingLegal(true);
    try {
      await axios.put(`${API}/partenaire/settings/legal`, legalForm, { headers: getHeaders() });
      toast.success('Informations légales enregistrées !');
      const res = await axios.get(`${API}/partenaire/settings`, { headers: getHeaders() });
      setSettings(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde'); }
    finally { setSavingLegal(false); }
  };

  const saveProfile = async () => {
    if (!profileForm.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSavingProfile(true);
    try {
      await axios.put(`${API}/partenaire/profile`, profileForm, { headers: getHeaders() });
      toast.success('Profil mis à jour !');
      const res = await axios.get(`${API}/partenaire/settings`, { headers: getHeaders() });
      setSettings(res.data);
      setProfileForm(f => ({ ...f, nom: res.data.nom || f.nom, prenom: res.data.prenom || f.prenom, telephone: res.data.phone || f.telephone }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde'); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async () => {
    if (!passwordForm.current_password) { toast.error('Entrez votre mot de passe actuel'); return; }
    if (passwordForm.new_password.length < 6) { toast.error('Le nouveau mot de passe doit faire au moins 6 caractères'); return; }
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/partenaire/password`, { current_password: passwordForm.current_password, new_password: passwordForm.new_password }, { headers: getHeaders() });
      toast.success('Mot de passe modifié !');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSavingPassword(false); }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const response = await axios.get(`${API}/partenaire/invoices/${inv.id}/pdf`, {
        headers: getHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${inv.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Erreur lors du téléchargement'); }
  };

  const exportAccountingData = async (format) => {
    try {
      const token = localStorage.getItem('partner_token');
      if (!token) { toast.error('Vous devez être connecté'); return; }
      setExporting(format);
      const params = new URLSearchParams({ format });
      if (exportYear !== 'all') {
        params.set('year', exportYear);
        if (exportMonth !== 'all') params.set('month', exportMonth);
      }
      const response = await fetch(`${API}/partenaire/accounting/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Export impossible');
      const count = response.headers.get('X-Invoice-Count');
      const periodLabel = exportYear === 'all' ? 'toutes' : exportMonth === 'all' ? exportYear : `${exportYear}-${String(exportMonth).padStart(2,'0')}`;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factures_partenaire_${periodLabel}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(count === '0' ? 'Aucune facture sur cette période' : `${count} facture(s) exportée(s)`);
    } catch (err) { toast.error("Erreur lors de l'export"); }
    finally { setExporting(null); }
  };

  const createNote = async () => {
    if (!newNote.title.trim() || !newNote.content.trim()) { toast.error('Titre et contenu requis'); return; }
    try {
      await axios.post(`${API}/partenaire/notes`, newNote, { headers: getHeaders() });
      toast.success('Note créée'); setNewNote({ title: '', content: '', client_email: '' }); setShowNoteForm(false);
      const res = await axios.get(`${API}/partenaire/notes`, { headers: getHeaders() });
      setNotes(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await axios.delete(`${API}/partenaire/notes/${noteId}`, { headers: getHeaders() });
      toast.success('Note supprimée');
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch { toast.error('Erreur'); }
  };

  const formatPrice = (p) => p ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p) : '0 €';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

  const filteredProjects = useMemo(() => {
    const sorted = [...projects].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    if (!projectSearch) return sorted;
    const s = projectSearch.toLowerCase();
    return sorted.filter(p => p.address?.toLowerCase().includes(s) || p.client_email?.toLowerCase().includes(s));
  }, [projects, projectSearch]);

  const filteredProspects = useMemo(() => {
    const estimation = [...prospects]
      .filter(p => (p.source || 'estimation') === 'estimation')
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    if (!prospectSearch) return estimation;
    const s = prospectSearch.toLowerCase();
    return estimation.filter(p => p.email?.toLowerCase().includes(s) || p.phone?.includes(s) || p.address?.toLowerCase().includes(s));
  }, [prospects, prospectSearch]);

  const conseilProspects = useMemo(() => {
    const conseil = [...prospects]
      .filter(p => p.source === 'conseil')
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    if (!prospectSearch) return conseil;
    const s = prospectSearch.toLowerCase();
    return conseil.filter(p => p.email?.toLowerCase().includes(s) || p.phone?.includes(s) || p.name?.toLowerCase().includes(s));
  }, [prospects, prospectSearch]);

  const sortedRadarProspects = useMemo(() => [...radarProspects].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [radarProspects]);
  const sortedAuditProjects = useMemo(() => [...projects].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [projects]);
  const sortedChantiers = useMemo(() => [...chantiers].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [chantiers]);
  const sortedConseils = useMemo(() => [...conseils].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [conseils]);

  // Prospects uniques (par email) toutes sources confondues
  const uniqueProspectCount = useMemo(() => {
    const emails = new Set();
    prospects.forEach(p => p.email && emails.add(p.email.toLowerCase()));
    radarProspects.forEach(p => p.email && emails.add(p.email.toLowerCase()));
    projects.forEach(p => p.client_email && emails.add(p.client_email.toLowerCase()));
    return emails.size;
  }, [prospects, radarProspects, projects]);

  // Vue d'ensemble : prospects regroupés par email avec badges d'actions
  const groupedProspects = useMemo(() => {
    const map = {};
    const addAction = (email, action, date) => {
      if (!email) return;
      const key = email.toLowerCase();
      if (!map[key]) map[key] = { email, actions: [], date: date || '' };
      if (!map[key].actions.includes(action)) map[key].actions.push(action);
      if ((date || '') > map[key].date) map[key].date = date || '';
    };
    prospects.forEach(p => {
      const label = p.source === 'conseil' ? 'Conseil' : p.source === 'radar' ? 'Radar' : 'Estimation';
      addAction(p.email, label, p.created_at);
    });
    radarProspects.forEach(p => addAction(p.email, 'Radar', p.created_at));
    projects.forEach(p => addAction(p.client_email, 'Audit', p.created_at));
    return Object.values(map).sort((a, b) => (b.date > a.date ? 1 : -1));
  }, [prospects, radarProspects, projects]);

  const unreadMessages = messages.filter(m => !m.read && m.from === 'admin').length;

  // Rafraîchir les messages hotline (marque comme lu côté serveur + vide le badge)
  const refreshMessages = async () => {
    try {
      const res = await axios.get(`${API}/partenaire/hotline/messages`, { headers: getHeaders() });
      setMessages(res.data);
    } catch (e) { /* silent */ }
  };

  // Dès qu'on ouvre l'onglet Hotline → rafraîchir pour vider le badge rouge
  // + polling automatique toutes les 10 secondes
  useEffect(() => {
    if (activeTab !== 'hotline') return;
    refreshMessages();
    const interval = setInterval(refreshMessages, 10000);
    return () => clearInterval(interval);
  }, [activeTab]);
  const copyUrl = () => { navigator.clipboard.writeText(`${window.location.origin}/p/${partner?.subdomain}`); toast.success('URL copiée !'); };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
    </div>
  );

  if (!partner) return null;

  const StatusBadge = ({ status }) => {
    const st = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return <Badge className={st.color}>{st.label}</Badge>;
  };

  const EmptyState = ({ icon: Icon, title, desc }) => (
    <div className="text-center py-16 text-gray-500">
      <Icon className="w-14 h-14 mx-auto mb-3 opacity-20" />
      <h3 className="font-medium mb-1">{title}</h3>
      <p className="text-sm">{desc}</p>
    </div>
  );

  // Calcule si le texte doit être noir ou blanc selon la luminosité du fond
  const getContrastColor = (hex) => {
    if (!hex) return '#ffffff';
    const h = hex.replace('#', '');
    const r = parseInt(h.substr(0, 2), 16);
    const g = parseInt(h.substr(2, 2), 16);
    const b = parseInt(h.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.55 ? '#1a1a2e' : '#ffffff';
  };
  const headerBg = settings.header_color || settings.primary_color || partner?.primary_color || '#0f1d3c';
  const headerText = getContrastColor(headerBg);
  const headerMuted = headerText === '#ffffff' ? 'rgba(255,255,255,0.65)' : 'rgba(26,26,46,0.6)';
  const headerBtnBg = headerText === '#ffffff' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.09)';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="partner-dashboard">
      {/* Header */}
      <header className="shadow-lg" style={{ backgroundColor: headerBg }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <button onClick={() => setMobileSidebarOpen(true)} style={{ backgroundColor: headerBtnBg, color: headerText }} className="w-9 h-9 rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity lg:hidden" data-testid="partner-mobile-menu">
              <Menu className="w-5 h-5" />
            </button>
            <button onClick={() => setActiveTab('overview')} style={{ backgroundColor: headerBtnBg }} className="w-9 h-9 rounded-lg items-center justify-center hover:opacity-80 transition-opacity hidden sm:flex overflow-hidden" data-testid="partner-home-btn">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-5 h-5" style={{ color: headerText }} />
              )}
            </button>
            <button onClick={() => setActiveTab('overview')} className="text-left">
              <h1 className="font-bold text-base sm:text-lg tracking-tight" style={{ color: headerText }}>ESPACE PARTENAIRE</h1>
              <p className="text-xs truncate max-w-[140px] sm:max-w-none" style={{ color: headerMuted }}>{partner.company_name || partner.full_name}</p>
            </button>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            {partner.subdomain && (
              <button onClick={copyUrl} style={{ backgroundColor: headerBtnBg, color: headerText }} className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium hover:opacity-80 transition-opacity" data-testid="copy-partner-url">
                <Copy className="w-3.5 h-3.5" />/p/{partner.subdomain}
              </button>
            )}
            {partner.subdomain && (
              <a href={`${window.location.origin}/p/${partner.subdomain}`} target="_blank" rel="noopener noreferrer"
                style={{ backgroundColor: headerBtnBg, color: headerText }}
                className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium hover:opacity-80 transition-opacity" data-testid="visit-partner-page">
                <ExternalLink className="w-3.5 h-3.5" /><span className="hidden sm:inline">Ma Page</span>
              </a>
            )}
            <button onClick={handleLogout} style={{ backgroundColor: headerBtnBg, color: headerMuted }} className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium hover:opacity-80 transition-opacity" data-testid="partner-logout">
              <LogOut className="w-4 h-4" /><span className="ml-1.5 hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      {/* Bannière mode démo */}
      {settings.subscription_status === 'demo' && (() => {
        const expiresAt = settings.demo_expires_at ? new Date(settings.demo_expires_at) : null;
        const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24))) : null;
        const expireLabel = expiresAt ? expiresAt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
        return (
          <div className="bg-amber-500 text-white px-4 py-2.5" data-testid="demo-banner">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className="text-lg">⏳</span>
                <span>
                  Mode démo — expire le <strong>{expireLabel}</strong>
                  {daysLeft !== null && (
                    <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs font-bold">
                      {daysLeft} jour{daysLeft > 1 ? 's' : ''} restant{daysLeft > 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              </div>
              <button
                data-testid="demo-subscribe-btn"
                disabled={subscribing}
                onClick={async () => {
                  setSubscribing(true);
                  try {
                    const res = await axios.post(`${API}/partenaire/subscribe`, {}, { headers: getHeaders() });
                    if (res.data.checkout_url) window.location.href = res.data.checkout_url;
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Erreur lors de la création du paiement');
                  } finally { setSubscribing(false); }
                }}
                className="flex items-center gap-1.5 bg-white text-amber-600 font-bold text-sm px-4 py-1.5 rounded-lg hover:bg-amber-50 transition-colors shrink-0"
              >
                {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Souscrire un abonnement
              </button>
            </div>
          </div>
        );
      })()}

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 flex gap-4 sm:gap-6">
        <PartnerSidebar activeTab={activeTab} onTabChange={setActiveTab} mobileOpen={false} onMobileClose={() => setMobileSidebarOpen(false)} badges={{ hotline: unreadMessages, chantiers: stats.total_chantiers, conseil: stats.total_conseil }} domainEnabled={!!partner?.domain_enabled} isInternational={!!(settings.country && settings.country !== 'france')} />
        <div className="flex-1 min-w-0">

          {/* ===== VUE D'ENSEMBLE ===== */}
          {activeTab === 'overview' && (
            <div className="space-y-6" data-testid="partner-overview">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Vue d'ensemble</h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Prospects', value: uniqueProspectCount, icon: TrendingUp, bg: 'bg-purple-100', ic: 'text-purple-600', sub: 'contacts uniques', tab: 'prospects_all' },
                  { label: 'Dossiers', value: stats.total_projects || 0, icon: FolderOpen, bg: 'bg-blue-100', ic: 'text-blue-600', sub: `${stats.pending_projects || 0} en attente`, tab: 'projects' },
                  { label: 'Estimation', value: stats.prospects_estimation || 0, icon: Calculator, bg: 'bg-emerald-100', ic: 'text-emerald-600', sub: "demandes d'estimation", tab: 'prospects_estimation' },
                  ...( !(settings.country && settings.country !== 'france') ? [
                    { label: 'Radar Foncier', value: stats.total_radar || 0, icon: Navigation, bg: 'bg-amber-100', ic: 'text-amber-600', sub: 'recherches parcelles', tab: 'prospects_radar' },
                    { label: 'Conseil / Travaux', value: stats.prospects_conseil || 0, icon: Shield, bg: 'bg-orange-100', ic: 'text-orange-600', sub: 'demandes conseil', tab: 'prospects_conseil' },
                  ] : []),
                  { label: 'Mes Commerciaux', value: stats.commerciaux_count || 0, icon: Users, bg: 'bg-indigo-100', ic: 'text-indigo-600', sub: 'commerciaux actifs', tab: 'commerciaux' },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <Card key={s.label} className={s.tab ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} onClick={s.tab ? () => setActiveTab(s.tab) : undefined}>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-xl ${s.bg}`}><Icon className={`w-5 h-5 ${s.ic}`} /></div>
                          <div>
                            <p className="text-xs text-gray-500">{s.label}</p>
                            <p className="text-xl font-bold text-gray-900">{s.value}</p>
                            {s.sub && <p className="text-[10px] text-gray-400">{s.sub}</p>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              {/* Recent items */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Derniers Prospects</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setActiveTab('prospects_all')} className="text-purple-600 text-xs">Voir tout <ArrowUpRight className="w-3 h-3 ml-1" /></Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {groupedProspects.length === 0 ? <p className="text-sm text-gray-400 py-4 text-center">Aucun prospect</p> : (
                      <div className="divide-y">
                        {groupedProspects.slice(0, 5).map((p) => (
                          <div key={p.email} className="flex items-center justify-between py-2.5 gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{p.email}</p>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {p.actions.map(a => {
                                  const colors = { Estimation: 'bg-emerald-100 text-emerald-700', Radar: 'bg-amber-100 text-amber-700', Conseil: 'bg-violet-100 text-violet-700', Audit: 'bg-blue-100 text-blue-700' };
                                  return <span key={a} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors[a] || 'bg-gray-100 text-gray-600'}`}>{a}</span>;
                                })}
                              </div>
                            </div>
                            <span className="text-xs text-gray-400 shrink-0">{formatDate(p.date)}</span>
                          </div>
                        ))}
                        {groupedProspects.length > 5 && (
                          <p className="text-xs text-gray-400 text-center pt-2">et {groupedProspects.length - 5} autre{groupedProspects.length - 5 > 1 ? 's' : ''}…</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Dossiers récents</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setActiveTab('projects')} className="text-purple-600 text-xs">Voir tout <ArrowUpRight className="w-3 h-3 ml-1" /></Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {projects.length === 0 ? <p className="text-sm text-gray-400 py-4 text-center">Aucun dossier</p> : (
                      <div className="divide-y">{projects.slice(0, 5).map((p) => (
                        <div key={p.id} className="flex items-center justify-between py-2.5">
                          <div><p className="font-medium text-sm truncate max-w-[200px]">{p.address}</p><p className="text-xs text-gray-400">{p.client_email || '-'}</p></div>
                          <StatusBadge status={p.status} />
                        </div>
                      ))}</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* ===== DOSSIERS ===== */}
          {activeTab === 'projects' && (
            <div className="space-y-4" data-testid="partner-projects">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Dossiers</h2>
                <div className="relative w-64"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Rechercher..." value={projectSearch} onChange={(e) => { setProjectSearch(e.target.value); projectsPagination.reset(); }} className="pl-9" data-testid="project-search" />
                </div>
              </div>
              <Card><CardContent className="p-0">
                {filteredProjects.length === 0 ? <EmptyState icon={FolderOpen} title="Aucun dossier" desc="Les dossiers soumis via votre page apparaîtront ici." /> : (
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b bg-gray-50/50">
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Adresse</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Prix</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Comm</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Suppr.</th>
                    </tr></thead>
                    <tbody>{projectsPagination.paginate(filteredProjects).map((p) => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="p-3"><p className="font-medium text-sm truncate max-w-[200px]">{p.address}</p>{p.surface > 0 && <p className="text-xs text-gray-400">{p.surface} m²</p>}</td>
                        <td className="p-3"><p className="text-sm">{p.client_email || '-'}</p>{p.client_phone && <span className="text-xs text-purple-600">{p.client_phone}</span>}</td>
                        <td className="p-3 text-sm font-medium">{formatPrice(p.purchase_price)}</td>
                        <td className="p-3"><StatusBadge status={p.status} /></td>
                        <td className="p-3 text-sm text-gray-500">{formatDate(p.created_at)}</td>
                        <td className="p-3">
                          <button
                            onClick={() => setCommProject(p)}
                            className="px-3 py-1.5 rounded text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 flex items-center gap-1"
                            data-testid={`partner-open-comm-${p.id}`}
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            Communication
                          </button>
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => handleDeleteProject(p)}
                            disabled={deletingProjectId === p.id}
                            className="px-2 py-1.5 rounded text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1 disabled:opacity-50"
                            data-testid={`partner-delete-project-${p.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            {deletingProjectId === p.id ? '...' : 'Supprimer'}
                          </button>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </CardContent></Card>
              <Pagination currentPage={projectsPagination.page} totalItems={filteredProjects.length} itemsPerPage={projectsPagination.perPage} onPageChange={projectsPagination.setPage} onItemsPerPageChange={projectsPagination.setPerPage} />
            </div>
          )}


          {/* ===== MES COMMERCIAUX ===== */}
          {activeTab === 'commerciaux' && (
            <CommerciauxTab
              getHeaders={getHeaders}
              primaryColor={settings.primary_color || '#7C3AED'}
              partnerSubdomain={partner?.subdomain || ''}
            />
          )}


          {/* ===== TOUS LES PROSPECTS ===== */}
          {activeTab === 'prospects_all' && (
            <div className="space-y-4" data-testid="partner-prospects-all">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Tous les Prospects <Badge className="ml-2 bg-purple-100 text-purple-700">{uniqueProspectCount}</Badge></h2>
              </div>
              {groupedProspects.length === 0 ? (
                <div className="text-center py-12 text-gray-400">Aucun prospect pour l'instant.</div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Dernière activité</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {groupedProspects.map((p) => (
                        <tr key={p.email} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800">{p.email}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {p.actions.map(a => {
                                const colors = { Estimation: 'bg-emerald-100 text-emerald-700', Radar: 'bg-amber-100 text-amber-700', Conseil: 'bg-violet-100 text-violet-700', Audit: 'bg-blue-100 text-blue-700' };
                                return <span key={a} className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[a] || 'bg-gray-100 text-gray-600'}`}>{a}</span>;
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(p.date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ===== PROSPECTS ESTIMATION ===== */}
          {activeTab === 'prospects_estimation' && (
            <div className="space-y-4" data-testid="partner-prospects-estimation">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Prospects Estimation <Badge className="ml-2 bg-emerald-100 text-emerald-700">{filteredProspects.length}</Badge></h2>
                <div className="relative w-64"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Rechercher email, tél, adresse..." value={prospectSearch} onChange={(e) => { setProspectSearch(e.target.value); estimationPagination.reset(); }} className="pl-9" />
                </div>
              </div>
              <Card><CardContent className="p-0">
                {filteredProspects.length === 0 ? <EmptyState icon={Calculator} title="Aucun prospect estimation" desc="Les demandes d'estimation via votre page apparaîtront ici." /> : (
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b bg-gray-50/50">
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Téléphone</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Adresse</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Type bien</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Estimation</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    </tr></thead>
                    <tbody>{estimationPagination.paginate(filteredProspects).map((p) => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="p-3"><a href={`mailto:${p.email}`} className="text-sm text-emerald-600 hover:underline flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{p.email}</a></td>
                        <td className="p-3 text-sm">{p.phone || '-'}</td>
                        <td className="p-3"><p className="text-sm truncate max-w-[180px]">{p.address || '-'}</p></td>
                        <td className="p-3 text-sm capitalize">{p.property_type || '-'}</td>
                        <td className="p-3 text-sm font-medium">{p.estimated_price ? formatPrice(p.estimated_price) : '-'}</td>
                        <td className="p-3 text-sm text-gray-500">{formatDate(p.created_at)}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </CardContent></Card>
              <Pagination currentPage={estimationPagination.page} totalItems={filteredProspects.length} itemsPerPage={estimationPagination.perPage} onPageChange={estimationPagination.setPage} onItemsPerPageChange={estimationPagination.setPerPage} />
            </div>
          )}

          {/* ===== PROSPECTS RADAR FONCIER ===== */}
          {activeTab === 'prospects_radar' && (
            <div className="space-y-4" data-testid="partner-prospects-radar">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Prospects Radar Foncier <Badge className="ml-2 bg-amber-100 text-amber-700">{radarProspects.length}</Badge></h2>
              </div>
              <Card><CardContent className="p-0">
                {radarProspects.length === 0 ? <EmptyState icon={Navigation} title="Aucun prospect Radar" desc="Les recherches de parcelles via votre page apparaîtront ici." /> : (
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b bg-gray-50/50">
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Téléphone</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Parcelle recherchée</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    </tr></thead>
                    <tbody>{radarPagination.paginate(sortedRadarProspects).map((p) => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="p-3"><a href={`mailto:${p.email}`} className="text-sm text-amber-600 hover:underline flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{p.email}</a></td>
                        <td className="p-3 text-sm">{p.phone || '-'}</td>
                        <td className="p-3 text-sm text-gray-600">
                          <p>{p.adresse_saisie || p.address || p.parcelle_ref || '-'}</p>
                          {p.adresse_saisie && (p.address || p.parcelle_ref) && (
                            <p className="text-xs text-gray-400 italic mt-0.5">{p.address || p.parcelle_ref}</p>
                          )}
                        </td>
                        <td className="p-3 text-sm text-gray-500">{formatDate(p.created_at)}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </CardContent></Card>
              <Pagination currentPage={radarPagination.page} totalItems={sortedRadarProspects.length} itemsPerPage={radarPagination.perPage} onPageChange={radarPagination.setPage} onItemsPerPageChange={radarPagination.setPerPage} />
            </div>
          )}

          {/* ===== PROSPECTS AUDIT ===== */}
          {activeTab === 'prospects_audit' && (
            <div className="space-y-4" data-testid="partner-prospects-audit">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  Prospects Audit
                  <Badge className="ml-2 bg-blue-100 text-blue-700">{projects.length}</Badge>
                </h2>
              </div>
              <Card><CardContent className="p-0">
                {projects.length === 0 ? (
                  <EmptyState icon={FileText} title="Aucun prospect Audit" desc="Les demandes d'audit reçues via votre page apparaîtront ici." />
                ) : (
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b bg-gray-50/50">
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Adresse</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Surface / Prix</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="p-3"></th>
                    </tr></thead>
                    <tbody>{auditPagination.paginate(sortedAuditProjects).map((p) => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="p-3">
                          <p className="text-sm font-medium">{p.client_name || '—'}</p>
                          <a href={`mailto:${p.client_email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                            <Mail className="w-3 h-3" />{p.client_email || '—'}
                          </a>
                          {p.client_phone && <p className="text-xs text-gray-400">{p.client_phone}</p>}
                        </td>
                        <td className="p-3">
                          <p className="text-sm max-w-[180px] truncate font-medium">{p.address}</p>
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {p.surface ? `${p.surface} m²` : '—'}
                          {p.purchase_price ? ` · ${formatPrice(p.purchase_price)}` : ''}
                        </td>
                        <td className="p-3"><StatusBadge status={p.status || 'pending'} /></td>
                        <td className="p-3 text-sm text-gray-500">{formatDate(p.created_at)}</td>
                        <td className="p-3">
                          <button
                            onClick={() => navigate('/partenaire/expert')}
                            className="px-3 py-1.5 rounded text-xs font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200"
                            data-testid={`partner-open-audit-${p.id}`}
                          >
                            Voir dossier
                          </button>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </CardContent></Card>
              <Pagination currentPage={auditPagination.page} totalItems={sortedAuditProjects.length} itemsPerPage={auditPagination.perPage} onPageChange={auditPagination.setPage} onItemsPerPageChange={auditPagination.setPerPage} />
            </div>
          )}

          {/* ===== PROSPECTS CONSEIL / TRAVAUX ===== */}
          {activeTab === 'prospects_conseil' && (
            <div className="space-y-4" data-testid="partner-prospects-conseil">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Prospects Conseil / Travaux <Badge className="ml-2 bg-violet-100 text-violet-700">{conseilProspects.length}</Badge></h2>
                <div className="relative w-64"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Rechercher nom, email, tél..." value={prospectSearch} onChange={(e) => { setProspectSearch(e.target.value); conseilPropagination.reset(); }} className="pl-9" />
                </div>
              </div>
              <Card><CardContent className="p-0">
                {conseilProspects.length === 0 ? <EmptyState icon={Shield} title="Aucun prospect Conseil" desc="Les demandes de conseil stratégique via votre page apparaîtront ici." /> : (
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b bg-gray-50/50">
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Nom</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Téléphone</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    </tr></thead>
                    <tbody>{conseilPropagination.paginate(conseilProspects).map((p) => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="p-3 text-sm font-medium">{p.name || '-'}</td>
                        <td className="p-3"><a href={`mailto:${p.email}`} className="text-sm text-violet-600 hover:underline flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{p.email}</a></td>
                        <td className="p-3 text-sm">{p.phone || '-'}</td>
                        <td className="p-3 text-sm text-gray-500">{formatDate(p.created_at)}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </CardContent></Card>
              <Pagination currentPage={conseilPropagination.page} totalItems={conseilProspects.length} itemsPerPage={conseilPropagination.perPage} onPageChange={conseilPropagination.setPage} onItemsPerPageChange={conseilPropagination.setPerPage} />
            </div>
          )}

          {/* ===== CHANTIERS VIP ===== */}
          {activeTab === 'chantiers' && (
            <div className="space-y-4" data-testid="partner-chantiers">
              <h2 className="text-xl font-bold text-gray-900">Chantiers VIP <Badge className="ml-2 bg-amber-100 text-amber-700">{chantiers.length}</Badge></h2>
              <Card><CardContent className="p-0">
                {chantiers.length === 0 ? <EmptyState icon={Shield} title="Aucun chantier VIP" desc="Les demandes de suivi de travaux apparaîtront ici." /> : (
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b bg-gray-50/50">
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Adresse</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Budget</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr></thead>
                    <tbody>{chantiersPagination.paginate(sortedChantiers).map((c) => (
                      <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="p-3 font-medium text-sm">{c.address}</td>
                        <td className="p-3 text-sm">{c.email || '-'}<br/><span className="text-xs text-gray-400">{c.phone}</span></td>
                        <td className="p-3 text-sm">{c.budget || '-'}</td>
                        <td className="p-3"><StatusBadge status={c.status || 'nouveau'} /></td>
                        <td className="p-3 text-sm text-gray-500">{formatDate(c.created_at)}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            {c.email && <a href={`mailto:${c.email}`} className="p-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors" title="Contacter"><Mail className="w-3.5 h-3.5" /></a>}
                            {c.phone && <a href={`tel:${c.phone}`} className="p-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors" title="Appeler"><Phone className="w-3.5 h-3.5" /></a>}
                            <select value={c.status || 'nouveau'} onChange={async (e) => {
                              try {
                                await axios.put(`${API}/partenaire/chantiers/${c.id}/status`, { status: e.target.value }, { headers: getHeaders() });
                                setChantiers(prev => prev.map(x => x.id === c.id ? { ...x, status: e.target.value } : x));
                                toast.success('Statut mis à jour');
                              } catch { toast.error('Erreur'); }
                            }} className="text-xs border rounded-lg px-2 py-1 bg-white cursor-pointer">
                              <option value="nouveau">Nouveau</option>
                              <option value="vu">Vu</option>
                              <option value="en_cours">En cours</option>
                              <option value="traite">Traité</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </CardContent></Card>
              <Pagination currentPage={chantiersPagination.page} totalItems={sortedChantiers.length} itemsPerPage={chantiersPagination.perPage} onPageChange={chantiersPagination.setPage} onItemsPerPageChange={chantiersPagination.setPerPage} />
            </div>
          )}

          {/* ===== CONSEIL VIP ===== */}
          {activeTab === 'conseil' && (
            <div className="space-y-4" data-testid="partner-conseil">
              <h2 className="text-xl font-bold text-gray-900">Conseil VIP <Badge className="ml-2 bg-purple-100 text-purple-700">{conseils.length}</Badge></h2>
              <Card><CardContent className="p-0">
                {conseils.length === 0 ? <EmptyState icon={Award} title="Aucune demande de conseil" desc="Les demandes de conseil stratégique apparaîtront ici." /> : (
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b bg-gray-50/50">
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr></thead>
                    <tbody>{conseilsDemandsPagination.paginate(sortedConseils).map((c) => (
                      <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="p-3"><Badge className="bg-gray-100 text-gray-700 text-xs capitalize">{c.conseil_type || '-'}</Badge></td>
                        <td className="p-3 text-sm">{c.email || '-'}<br/><span className="text-xs text-gray-400">{c.phone}</span></td>
                        <td className="p-3 text-sm truncate max-w-[200px]">{c.description || '-'}</td>
                        <td className="p-3"><StatusBadge status={c.status || 'nouveau'} /></td>
                        <td className="p-3 text-sm text-gray-500">{formatDate(c.created_at)}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            {c.email && <a href={`mailto:${c.email}`} className="p-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors" title="Contacter"><Mail className="w-3.5 h-3.5" /></a>}
                            {c.phone && <a href={`tel:${c.phone}`} className="p-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors" title="Appeler"><Phone className="w-3.5 h-3.5" /></a>}
                            <select value={c.status || 'nouveau'} onChange={async (e) => {
                              try {
                                await axios.put(`${API}/partenaire/conseil-demands/${c.id}/status`, { status: e.target.value }, { headers: getHeaders() });
                                setConseils(prev => prev.map(x => x.id === c.id ? { ...x, status: e.target.value } : x));
                                toast.success('Statut mis à jour');
                              } catch { toast.error('Erreur'); }
                            }} className="text-xs border rounded-lg px-2 py-1 bg-white cursor-pointer">
                              <option value="nouveau">Nouveau</option>
                              <option value="vu">Vu</option>
                              <option value="en_cours">En cours</option>
                              <option value="traite">Traité</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </CardContent></Card>
              <Pagination currentPage={conseilsDemandsPagination.page} totalItems={sortedConseils.length} itemsPerPage={conseilsDemandsPagination.perPage} onPageChange={conseilsDemandsPagination.setPage} onItemsPerPageChange={conseilsDemandsPagination.setPerPage} />
            </div>
          )}

          {/* ===== NOTES ===== */}
          {activeTab === 'notes' && (
            <div className="space-y-4" data-testid="partner-notes">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Notes <Badge className="ml-2 bg-gray-100 text-gray-700">{notes.length}</Badge></h2>
                <Button onClick={() => setShowNoteForm(!showNoteForm)} className="bg-[#0f1d3c] hover:bg-[#162a4f]" data-testid="add-note-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nouvelle note
                </Button>
              </div>
              {showNoteForm && (
                <Card className="border-2 border-blue-200">
                  <CardContent className="p-4 space-y-3">
                    <Input value={newNote.title} onChange={(e) => setNewNote(n => ({ ...n, title: e.target.value }))} placeholder="Titre de la note" data-testid="note-title-input" />
                    <Textarea value={newNote.content} onChange={(e) => setNewNote(n => ({ ...n, content: e.target.value }))} placeholder="Contenu..." rows={3} data-testid="note-content-input" />
                    <Input value={newNote.client_email} onChange={(e) => setNewNote(n => ({ ...n, client_email: e.target.value }))} placeholder="Email client (optionnel)" />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setShowNoteForm(false)}>Annuler</Button>
                      <Button size="sm" onClick={createNote} className="bg-[#0f1d3c]" data-testid="save-note-btn">Enregistrer</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {notes.length === 0 && !showNoteForm ? (
                <Card><CardContent className="p-0"><EmptyState icon={StickyNote} title="Aucune note" desc="Créez des notes pour suivre vos échanges clients." /></CardContent></Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  {notes.map((n) => (
                    <Card key={n.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{n.title}</h3>
                          <button onClick={() => deleteNote(n.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <p className="text-sm text-gray-600 whitespace-pre-line line-clamp-3">{n.content}</p>
                        <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                          {n.client_email && <span className="text-purple-600">{n.client_email}</span>}
                          <span>{formatDate(n.created_at)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== COMPTABILITÉ ===== */}
          {activeTab === 'accounting' && (
            <div className="space-y-4" data-testid="partner-accounting">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="text-xl font-bold text-gray-900">Comptabilité</h2>
                {/* Filtres + Export */}
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={exportYear}
                    onChange={e => setExportYear(e.target.value)}
                    className="h-9 rounded-md border border-gray-200 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="export-year-select"
                  >
                    <option value="all">Toutes les années</option>
                    {[2026, 2025, 2024, 2023].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  {exportYear !== 'all' && (
                    <select
                      value={exportMonth}
                      onChange={e => setExportMonth(e.target.value)}
                      className="h-9 rounded-md border border-gray-200 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="export-month-select"
                    >
                      <option value="all">Tous les mois</option>
                      {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportAccountingData('csv')}
                    disabled={!!exporting}
                    data-testid="export-csv-btn"
                    className="flex items-center gap-1.5"
                  >
                    <Download className="w-4 h-4" />{exporting === 'csv' ? 'Export...' : 'CSV'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => exportAccountingData('zip')}
                    disabled={!!exporting}
                    data-testid="export-zip-btn"
                    className="bg-[#0f1d3c] hover:bg-[#162a4f] flex items-center gap-1.5"
                  >
                    <Archive className="w-4 h-4" />{exporting === 'zip' ? 'Export...' : 'ZIP + PDF'}
                  </Button>
                </div>
              </div>
              
              {/* Stats Cards - HT / TVA / TTC / Encaissé / En attente */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: 'CA HT', value: formatPrice(accounting.total_ht), bg: 'bg-blue-100', ic: 'text-blue-600', icon: Euro },
                  { label: 'TVA (20%)', value: formatPrice(accounting.total_tva), bg: 'bg-purple-100', ic: 'text-purple-600', icon: Receipt },
                  { label: 'CA TTC', value: formatPrice(accounting.total_ttc), bg: 'bg-indigo-100', ic: 'text-indigo-600', icon: TrendingUp },
                  { label: 'Encaissé', value: formatPrice(accounting.total_paid), bg: 'bg-emerald-100', ic: 'text-emerald-600', icon: CheckCircle },
                  { label: 'En attente', value: formatPrice(accounting.total_pending), bg: 'bg-amber-100', ic: 'text-amber-600', icon: Clock },
                ].map(s => {
                  const Icon = s.icon;
                  return (<Card key={s.label}><CardContent className="p-4"><div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${s.bg}`}><Icon className={`w-4 h-4 ${s.ic}`} /></div>
                    <div><p className="text-xs text-gray-500">{s.label}</p><p className="text-lg font-bold text-gray-900">{s.value}</p></div>
                  </div></CardContent></Card>);
                })}
              </div>

              {/* Monthly Breakdown */}
              {accounting.monthly_breakdown && Object.keys(accounting.monthly_breakdown).length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Récapitulatif Mensuel</h3>
                    <div className="overflow-x-auto"><table className="w-full">
                      <thead><tr className="border-b bg-gray-50/50">
                        <th className="text-left p-2 text-xs font-semibold text-gray-500 uppercase">Mois</th>
                        <th className="text-right p-2 text-xs font-semibold text-gray-500 uppercase">HT</th>
                        <th className="text-right p-2 text-xs font-semibold text-gray-500 uppercase">TVA</th>
                        <th className="text-right p-2 text-xs font-semibold text-gray-500 uppercase">TTC</th>
                        <th className="text-right p-2 text-xs font-semibold text-gray-500 uppercase">Nb</th>
                      </tr></thead>
                      <tbody>{Object.entries(accounting.monthly_breakdown).sort(([a],[b]) => b.localeCompare(a)).map(([month, data]) => (
                        <tr key={month} className="border-b last:border-0 hover:bg-gray-50/50">
                          <td className="p-2 font-medium text-sm">{month}</td>
                          <td className="p-2 text-sm text-right">{formatPrice(data.ht)}</td>
                          <td className="p-2 text-sm text-right text-purple-600">{formatPrice(data.tva)}</td>
                          <td className="p-2 text-sm text-right font-bold">{formatPrice(data.ttc)}</td>
                          <td className="p-2 text-sm text-right text-gray-500">{data.count}</td>
                        </tr>
                      ))}</tbody>
                    </table></div>
                  </CardContent>
                </Card>
              )}
              
              {/* Invoices Table */}
              <Card><CardContent className="p-0">
                <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700">Factures ({accounting.invoices.length})</h3>
                </div>
                {accounting.invoices.length === 0 ? <EmptyState icon={Receipt} title="Aucune facture" desc="Vos factures apparaîtront ici." /> : (
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b bg-gray-50/50">
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">N°</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Client / Objet</th>
                      <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">HT</th>
                      <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">TVA</th>
                      <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">TTC</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-center p-3 text-xs font-semibold text-gray-500 uppercase">PDF</th>
                    </tr></thead>
                    <tbody>{accounting.invoices.map((inv) => {
                      const isSubscription = inv.type === 'partner_subscription';
                      return (
                        <tr key={inv.id} className={`border-b last:border-0 hover:bg-gray-50/50 transition-colors ${isSubscription ? 'bg-amber-50/30' : ''}`}>
                          <td className="p-3 text-sm font-mono">{inv.invoice_number || '-'}</td>
                          <td className="p-3">
                            {isSubscription
                              ? <Badge className="bg-amber-100 text-amber-700 text-xs">Abonnement</Badge>
                              : <Badge className="bg-gray-100 text-gray-600 text-xs capitalize">{inv.type}</Badge>
                            }
                          </td>
                          <td className="p-3 text-sm">
                            {isSubscription
                              ? <span className="text-amber-700 font-medium">{inv.description || 'Abonnement Marque Blanche'}</span>
                              : (inv.client_email || inv.client_name || '-')
                            }
                          </td>
                          <td className="p-3 text-sm text-right">{formatPrice(inv.subtotal)}</td>
                          <td className="p-3 text-sm text-right text-purple-600">{formatPrice(inv.tva_amount)}</td>
                          <td className="p-3 text-sm text-right font-bold">{formatPrice(inv.total)}</td>
                          <td className="p-3"><StatusBadge status={inv.status} /></td>
                          <td className="p-3 text-sm text-gray-500">{formatDate(inv.created_at)}</td>
                          <td className="p-3 text-center">
                            {inv.invoice_number ? (
                              <a
                                href={`${API}/partenaire/invoices/${inv.id}/pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { e.preventDefault(); downloadInvoicePdf(inv); }}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                                title="Télécharger PDF"
                                data-testid={`download-invoice-${inv.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            ) : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table></div>
                )}
              </CardContent></Card>
            </div>
          )}

          {/* ===== HOTLINE ===== */}
          {activeTab === 'hotline' && (
            <div className="space-y-4" data-testid="partner-hotline">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Headphones className="w-5 h-5 text-orange-600" /> Hotline Admin</h2>
                <button onClick={refreshMessages} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors" data-testid="hotline-refresh-btn">
                  <RefreshCw className="w-3.5 h-3.5" /> Actualiser
                </button>
              </div>
              <Card><CardContent className="p-5">
                <div className="bg-gray-50 rounded-xl p-4 mb-4 max-h-[450px] overflow-y-auto">
                  {messages.length === 0 ? (
                    <div className="text-center py-12 text-gray-400"><MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="font-medium">Aucun message</p><p className="text-sm mt-1">{"Posez vos questions, l'administrateur vous répondra ici."}</p></div>
                  ) : (
                    <div className="space-y-3">{messages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.from === 'partner' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] p-3 rounded-xl ${msg.from === 'partner' ? 'bg-[#0f1d3c] text-white' : 'bg-white border shadow-sm'}`}>
                          <p className="text-sm">{msg.message}</p>
                          <p className={`text-[10px] mt-1 ${msg.from === 'partner' ? 'text-gray-400' : 'text-gray-400'}`}>{formatDateTime(msg.created_at)}</p>
                        </div>
                      </div>
                    ))}</div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Écrivez votre message..." className="resize-none" rows={2} data-testid="hotline-message-input" />
                  <Button onClick={sendHotlineMessage} disabled={sendingMessage || !newMessage.trim()} className="bg-[#0f1d3c] hover:bg-[#162a4f] shrink-0" data-testid="hotline-send-btn"><Send className="w-4 h-4" /></Button>
                </div>
              </CardContent></Card>
            </div>
          )}

          {/* ===== MON PROFIL ===== */}
          {activeTab === 'profile' && (
            <div className="space-y-6" data-testid="partner-profile">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-blue-600" /> Mon Profil
              </h2>

              {/* Infos du compte */}
              <Card><CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" /> Identité</h3>
                <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-3 mb-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Email (non modifiable)</p>
                    <p className="text-sm font-medium text-gray-800">{settings.email || partner?.email}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Prénom</label>
                    <Input value={profileForm.prenom} onChange={e => setProfileForm(f => ({ ...f, prenom: e.target.value }))} placeholder="Votre prénom" data-testid="profile-prenom" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Nom *</label>
                    <Input value={profileForm.nom} onChange={e => setProfileForm(f => ({ ...f, nom: e.target.value }))} placeholder="Votre nom de famille" data-testid="profile-nom" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Téléphone</label>
                    <Input value={profileForm.telephone} onChange={e => setProfileForm(f => ({ ...f, telephone: e.target.value }))} placeholder="Ex : 06 12 34 56 78" data-testid="profile-telephone" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Nom de la société</label>
                    <Input value={profileForm.company_name} onChange={e => setProfileForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Ex : Mon Expertise Immo" data-testid="profile-company" />
                  </div>
                </div>
                <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#0f1d3c] hover:bg-[#162a4f]" data-testid="save-profile-btn">
                  {savingProfile ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Enregistrer le profil</>}
                </Button>
              </CardContent></Card>

              {/* Changement mot de passe */}
              <Card><CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Shield className="w-4 h-4 text-orange-500" /> Changer le mot de passe</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Mot de passe actuel</label>
                    <div className="relative">
                      <Input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordForm.current_password}
                        onChange={e => setPasswordForm(f => ({ ...f, current_password: e.target.value }))}
                        placeholder="Votre mot de passe actuel"
                        data-testid="password-current"
                        className="pr-10"
                      />
                      <button type="button" onClick={() => setShowPasswords(s => ({ ...s, current: !s.current }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Nouveau mot de passe</label>
                    <div className="relative">
                      <Input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordForm.new_password}
                        onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))}
                        placeholder="Minimum 6 caractères"
                        data-testid="password-new"
                        className="pr-10"
                      />
                      <button type="button" onClick={() => setShowPasswords(s => ({ ...s, new: !s.new }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordForm.new_password && passwordForm.new_password.length < 6 && (
                      <p className="text-xs text-red-500 mt-1">Minimum 6 caractères</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Confirmer le nouveau mot de passe</label>
                    <div className="relative">
                      <Input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordForm.confirm_password}
                        onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))}
                        placeholder="Répétez le nouveau mot de passe"
                        data-testid="password-confirm"
                        className="pr-10"
                      />
                      <button type="button" onClick={() => setShowPasswords(s => ({ ...s, confirm: !s.confirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                      <p className="text-xs text-red-500 mt-1">Les mots de passe ne correspondent pas</p>
                    )}
                    {passwordForm.confirm_password && passwordForm.new_password === passwordForm.confirm_password && passwordForm.new_password.length >= 6 && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Mots de passe identiques</p>
                    )}
                  </div>
                </div>
                <Button onClick={savePassword} disabled={savingPassword || !passwordForm.current_password || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password}
                  className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-password-btn">
                  {savingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Modification...</> : <><Shield className="w-4 h-4 mr-2" /> Changer le mot de passe</>}
                </Button>
              </CardContent></Card>
            </div>
          )}

          {/* ===== INFOS LÉGALES ===== */}
          {activeTab === 'legal' && (
            <div className="space-y-6" data-testid="partner-legal">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" /> Informations Légales
              </h2>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                <strong>Important :</strong> Ces informations apparaissent sur les factures émises à vos clients. Elles doivent être exactes et conformes à votre immatriculation.
              </div>
              <Card><CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-600" /> Entreprise</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Nom de la société *</label>
                    <Input value={legalForm.company_name} onChange={e => setLegalForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Ex : Mon Expertise Immo SAS" data-testid="legal-company-name" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Forme juridique</label>
                    <select value={legalForm.legal_form} onChange={e => setLegalForm(f => ({ ...f, legal_form: e.target.value }))}
                      className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="legal-legal-form">
                      <option value="">-- Choisir --</option>
                      {['SAS','SARL','SASU','EURL','SCI','SA','Micro-entreprise','Auto-entrepreneur','EI','EIRL','Autre'].map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Capital social (€)</label>
                    <Input value={legalForm.capital_social} onChange={e => setLegalForm(f => ({ ...f, capital_social: e.target.value }))} placeholder="Ex : 10 000" data-testid="legal-capital" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Adresse siège social (numéro et rue)</label>
                    <Input value={legalForm.address} onChange={e => setLegalForm(f => ({ ...f, address: e.target.value }))} placeholder="Ex : 12 Rue de la Paix" data-testid="legal-address" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Code Postal / Ville</label>
                    <Input value={legalForm.city_postal} onChange={e => setLegalForm(f => ({ ...f, city_postal: e.target.value }))} placeholder="Ex : 75001 Paris" data-testid="legal-city-postal" />
                  </div>
                </div>
              </CardContent></Card>

              <Card><CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Immatriculation</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">SIRET *</label>
                    <Input value={legalForm.siret} onChange={e => setLegalForm(f => ({ ...f, siret: e.target.value }))} placeholder="Ex : 123 456 789 00012" data-testid="legal-siret" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">N° TVA Intracommunautaire</label>
                    <Input value={legalForm.tva_number} onChange={e => setLegalForm(f => ({ ...f, tva_number: e.target.value }))} placeholder="Ex : FR12 123456789" data-testid="legal-tva" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-gray-600 mb-1 block">RCS (Ville d'immatriculation)</label>
                    <Input value={legalForm.rcs_info} onChange={e => setLegalForm(f => ({ ...f, rcs_info: e.target.value }))} placeholder="Ex : Paris B 123 456 789" data-testid="legal-rcs" />
                  </div>
                </div>
              </CardContent></Card>

              <Card><CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-600" /> Coordonnées Bancaires</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-gray-600 mb-1 block">IBAN</label>
                    <Input value={legalForm.bank_iban} onChange={e => setLegalForm(f => ({ ...f, bank_iban: e.target.value }))} placeholder="Ex : FR76 3000 6000 0112 3456 7890 189" data-testid="legal-iban" className="font-mono" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">BIC / SWIFT</label>
                    <Input value={legalForm.bank_bic} onChange={e => setLegalForm(f => ({ ...f, bank_bic: e.target.value }))} placeholder="Ex : BNPAFRPPXXX" data-testid="legal-bic" className="font-mono" />
                  </div>
                </div>
              </CardContent></Card>

              {/* Aperçu pied de page facture */}
              {(legalForm.company_name || legalForm.siret) && (
                <Card className="border-blue-200 bg-blue-50/40"><CardContent className="p-4">
                  <p className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">Aperçu pied de page facture :</p>
                  <p className="text-xs text-gray-600 text-center italic">
                    {[
                      legalForm.company_name,
                      legalForm.legal_form && legalForm.capital_social ? `${legalForm.legal_form} au capital de ${legalForm.capital_social} €` : legalForm.legal_form,
                      legalForm.siret ? `SIRET : ${legalForm.siret}` : '',
                      legalForm.rcs_info ? `RCS ${legalForm.rcs_info}` : '',
                      legalForm.address,
                    ].filter(Boolean).join(' — ')}
                  </p>
                </CardContent></Card>
              )}

              <Button onClick={saveLegal} disabled={savingLegal} className="bg-[#0f1d3c] hover:bg-[#162a4f]" data-testid="save-legal-btn">
                {savingLegal ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Enregistrer les informations</>}
              </Button>
            </div>
          )}

          {/* ===== PERSONNALISATION ===== */}
          {activeTab === 'branding' && (
            <div className="space-y-6" data-testid="partner-branding">
              <h2 className="text-xl font-bold text-gray-900">Personnalisation</h2>
              <Card><CardContent className="p-5">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-600" /> Page Blanche</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">URL personnalisée</p>
                    <SubdomainEditor
                      currentSubdomain={settings.subdomain || partner?.subdomain || ''}
                      onSaved={(newSub) => {
                        setSettings(s => ({ ...s, subdomain: newSub }));
                        setPartner(p => ({ ...p, subdomain: newSub }));
                        const stored = JSON.parse(localStorage.getItem('partner_data') || '{}');
                        localStorage.setItem('partner_data', JSON.stringify({ ...stored, subdomain: newSub }));
                      }}
                      getHeaders={getHeaders}
                    />
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500 mb-1">Email</p><p className="text-sm font-medium">{partner?.email}</p></div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-5">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Camera className="w-4 h-4 text-blue-600" /> Logo</h3>
                <div className="flex items-start gap-4">
                  <div className="relative">
                    {logoPreview ? (
                      <div className="relative">
                        <img src={logoPreview} alt="Logo" className="h-20 w-auto object-contain bg-gray-50 rounded-xl p-2 border" />
                        {logoFile && <button onClick={() => { setLogoFile(null); setLogoPreview(settings.logo_url || null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"><X className="w-3 h-3" /></button>}
                      </div>
                    ) : <div className="h-20 w-20 bg-gray-50 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300"><Camera className="w-7 h-7 text-gray-400" /></div>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoSelect} className="hidden" id="logo-upload" />
                    <label htmlFor="logo-upload" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer text-sm"><Upload className="w-4 h-4" />{logoPreview ? 'Changer' : 'Ajouter'}</label>
                    {logoPreview && !logoFile && (
                      <button onClick={async () => {
                        if (!window.confirm('Supprimer le logo ?')) return;
                        try {
                          await axios.put(`${API}/partenaire/settings/branding`, { logo_url: '' }, { headers: getHeaders() });
                          setLogoPreview(null);
                          setSettings(s => ({ ...s, logo_url: '' }));
                          toast.success('Logo supprimé');
                        } catch { toast.error('Erreur'); }
                      }} className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm">
                        <X className="w-4 h-4" /> Supprimer
                      </button>
                    )}
                    <p className="text-xs text-gray-500">JPG, PNG, SVG (max 5MB)</p>
                  </div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-5">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Palette className="w-4 h-4 text-blue-600" /> Couleurs de marque</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Couleur principale <span className="text-xs text-gray-400">(header, boutons CTA, titres colorés)</span></p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {brandColors.map(c => (
                        <button key={c.value} onClick={() => setSelectedColor(c.value)}
                          className={`w-9 h-9 rounded-lg transition-all ${selectedColor === c.value ? 'ring-2 ring-blue-500 ring-offset-2 scale-110' : 'hover:scale-105'}`}
                          style={{ backgroundColor: c.value }} title={c.name} />
                      ))}
                      <input type="color" value={selectedColor} onChange={(e) => setSelectedColor(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-2 border-gray-200" title="Couleur personnalisée" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Couleur accent <span className="text-xs text-gray-400">(badges, surbrillances, éléments secondaires)</span></p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        { name: 'Or', value: '#F59E0B' }, { name: 'Corail', value: '#F97316' },
                        { name: 'Rose', value: '#EC4899' }, { name: 'Vert', value: '#10B981' },
                        { name: 'Cyan', value: '#06B6D4' }, { name: 'Lavande', value: '#8B5CF6' },
                        { name: 'Rouge', value: '#EF4444' }, { name: 'Lime', value: '#84CC16' },
                      ].map(c => (
                        <button key={c.value} onClick={() => setSelectedAccentColor(c.value)}
                          className={`w-9 h-9 rounded-lg transition-all ${selectedAccentColor === c.value ? 'ring-2 ring-blue-500 ring-offset-2 scale-110' : 'hover:scale-105'}`}
                          style={{ backgroundColor: c.value }} title={c.name} />
                      ))}
                      <input type="color" value={selectedAccentColor} onChange={(e) => setSelectedAccentColor(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-2 border-gray-200" title="Accent personnalisé" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Couleur bloc CTA <span className="text-xs text-gray-400">(section "Prêt à commencer" en bas de page)</span></p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        { name: 'Vert', value: '#10B981' }, { name: 'Bleu', value: '#3B82F6' },
                        { name: 'Violet', value: '#7C3AED' }, { name: 'Or', value: '#F59E0B' },
                        { name: 'Rouge', value: '#EF4444' }, { name: 'Marine', value: '#1E3A5F' },
                        { name: 'Ardoise', value: '#475569' }, { name: 'Noir', value: '#111827' },
                      ].map(c => (
                        <button key={c.value} onClick={() => setSelectedCtaColor(c.value)}
                          className={`w-9 h-9 rounded-lg transition-all ${selectedCtaColor === c.value ? 'ring-2 ring-blue-500 ring-offset-2 scale-110' : 'hover:scale-105'}`}
                          style={{ backgroundColor: c.value }} title={c.name} />
                      ))}
                      <input type="color" value={selectedCtaColor} onChange={(e) => setSelectedCtaColor(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-2 border-gray-200" title="CTA personnalisé" />
                    </div>
                    <div className="p-4 rounded-lg text-center text-white text-sm font-semibold" style={{ backgroundColor: selectedCtaColor }}>
                      Prêt à commencer ? — Aperçu
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Couleur en-tête (header) <span className="text-xs text-gray-400">(barre de navigation en haut de page — si vide, utilise la couleur principale)</span></p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        { name: 'Comme principale', value: '' },
                        { name: 'Blanc', value: '#FFFFFF' }, { name: 'Noir', value: '#111827' },
                        { name: 'Marine', value: '#1E3A5F' }, { name: 'Ardoise', value: '#334155' },
                        { name: 'Bleu roi', value: '#1D4ED8' }, { name: 'Bordeaux', value: '#9F1239' },
                        { name: 'Vert foncé', value: '#14532D' }, { name: 'Gris', value: '#6B7280' },
                      ].map(c => (
                        <button key={c.value} onClick={() => setSelectedHeaderColor(c.value)}
                          className={`w-9 h-9 rounded-lg transition-all border ${selectedHeaderColor === c.value ? 'ring-2 ring-blue-500 ring-offset-2 scale-110' : 'hover:scale-105'}`}
                          style={{ backgroundColor: c.value || '#e5e7eb' }}
                          title={c.name}>
                          {c.value === '' && <span className="text-gray-500 text-xs font-bold flex items-center justify-center h-full">=</span>}
                        </button>
                      ))}
                      <input type="color" value={selectedHeaderColor || selectedColor} onChange={(e) => setSelectedHeaderColor(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-2 border-gray-200" title="Couleur personnalisée" />
                    </div>
                    <div className="p-3 rounded-lg flex items-center justify-between px-4" style={{ backgroundColor: selectedHeaderColor || selectedColor }}>
                      <span className="font-bold text-sm" style={{ color: (selectedHeaderColor || selectedColor) === '#FFFFFF' ? '#111827' : 'white' }}>Moise Dhaouadi</span>
                      <span className="text-xs px-3 py-1 rounded-full border border-white/30" style={{ color: (selectedHeaderColor || selectedColor) === '#FFFFFF' ? '#111827' : 'white' }}>Installer l'app</span>
                    </div>
                  </div>

                  {/* Couleur du texte header / bandeau */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Couleur du texte (header & bandeau) <span className="text-xs text-gray-400">(laisser vide = contraste automatique)</span></p>
                    <p className="text-xs text-gray-400 mb-2">Par défaut, le texte est automatiquement noir ou blanc selon la lisibilité sur votre couleur de fond.</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        { name: 'Auto (recommandé)', value: '' },
                        { name: 'Blanc', value: '#FFFFFF' },
                        { name: 'Noir foncé', value: '#1F2937' },
                        { name: 'Gris foncé', value: '#374151' },
                        { name: 'Beige', value: '#F5F5DC' },
                        { name: 'Or', value: '#F59E0B' },
                      ].map(c => (
                        <button key={c.value} onClick={() => setSelectedTextColor(c.value)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all ${selectedTextColor === c.value ? 'ring-2 ring-blue-500 ring-offset-1' : 'hover:border-gray-400'}`}
                          style={{ backgroundColor: c.value || '#f3f4f6', color: c.value ? (c.value === '#FFFFFF' || c.value === '#F5F5DC' || c.value === '#F59E0B' ? '#111827' : '#FFFFFF') : '#374151', borderColor: selectedTextColor === c.value ? '#3B82F6' : '#e5e7eb' }}
                          title={c.name}>
                          {c.name}
                        </button>
                      ))}
                      <input type="color" value={selectedTextColor || '#FFFFFF'} onChange={(e) => setSelectedTextColor(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-2 border-gray-200" title="Couleur personnalisée" />
                    </div>
                    <div className="p-3 rounded-lg flex items-center gap-3 px-4" style={{ backgroundColor: selectedHeaderColor || selectedColor }}>
                      <span className="font-bold text-sm" style={{ color: selectedTextColor || (selectedHeaderColor || selectedColor) === '#FFFFFF' ? '#111827' : '#FFFFFF' }}>Aperçu du texte</span>
                      {!selectedTextColor && <span className="text-xs px-2 py-0.5 rounded bg-white/20 text-white/70">Auto</span>}
                    </div>
                  </div>

                  <div className="p-4 rounded-lg border flex items-center gap-3 flex-wrap" style={{ backgroundColor: selectedColor + '08' }}>
                    <button className="px-5 py-2 text-white rounded-lg text-sm font-medium" style={{ backgroundColor: selectedColor }}>Bouton principal</button>
                    <button className="px-5 py-2 text-white rounded-lg text-sm font-medium" style={{ backgroundColor: selectedAccentColor }}>Bouton accent</button>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: selectedAccentColor }}>Badge Pro</span>
                  </div>
                </div>
              </CardContent></Card>
              <Button onClick={saveBranding} disabled={savingBranding} className="bg-[#0f1d3c] hover:bg-[#162a4f]" data-testid="save-branding-btn">
                {savingBranding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Enregistrer la personnalisation</>}
              </Button>

              {/* ===== LUXE — Champs avancés ===== */}
              <div className="pt-2 border-t">
                <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-yellow-500">★</span> Personnalisation avancée
                </h3>
                <div className="space-y-5">

                  {/* Slogan */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-800">Textes & Contenu</h3>
                      <div className="flex items-center gap-2">
                        {/* Sélecteur de langue pour l'exemple */}
                        <div className="flex rounded-lg border border-blue-200 overflow-hidden text-xs">
                          {[{k:'fr',l:'FR'},{k:'ar',l:'AR'},{k:'en',l:'EN'}].map(opt => (
                            <button key={opt.k} onClick={() => setExampleLang(opt.k)}
                              className={`px-2 py-1.5 transition-colors ${exampleLang === opt.k ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
                              {opt.l}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => fillImmobilierDefaults(exampleLang)} className="text-xs px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 transition-colors flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5" />
                          Exemple immobilier
                        </button>
                      </div>
                    </div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Slogan / Accroche</label>
                    <p className="text-xs text-gray-400 mb-2">Affiché en grand dans le héro de votre page</p>
                    <input className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none" value={slogan} onChange={e => setSlogan(e.target.value)} placeholder="Ex : L'immobilier au cœur de vos projets depuis 1998" maxLength={100} />
                  </div>

                  {/* Téléphone */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Téléphone affiché sur la page</label>
                    <input className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none" value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="Ex : 04 91 00 00 00" />
                  </div>

                  {/* Email de contact */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Email de contact affiché sur la page</label>
                    <p className="text-xs text-gray-400 mb-2">Affiché dans le footer de votre page vitrine, à côté du téléphone</p>
                    <input
                      type="email"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                      value={contactEmail}
                      onChange={e => setContactEmail(e.target.value)}
                      placeholder="Ex : contact@monagence.fr"
                    />
                  </div>

                  {/* Site internet */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Site internet</label>
                    <p className="text-xs text-gray-400 mb-2">Affiché sous votre nom dans l'en-tête et le footer de votre page</p>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm pointer-events-none">🌐</span>
                      <input
                        className="w-full border rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                        value={websiteUrl}
                        onChange={e => setWebsiteUrl(e.target.value)}
                        placeholder="https://www.monagenceimmo.fr"
                        type="url"
                      />
                    </div>
                  </div>

                  {/* À propos */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Texte "À propos"</label>
                    <p className="text-xs text-gray-400 mb-2">Section dédiée sur votre page — présentation de l'agence, valeurs, histoire</p>
                    <textarea className="w-full border rounded-lg px-3 py-2 text-sm min-h-[100px] resize-none focus:ring-1 focus:ring-blue-400 focus:outline-none" value={aboutText} onChange={e => setAboutText(e.target.value)} placeholder="Ex : Fondée en 1998, notre agence accompagne les propriétaires et investisseurs dans leurs projets immobiliers..." />
                  </div>

                  {/* 3 Images */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Camera className="w-4 h-4 text-blue-600" /> Photos (3 emplacements)</h3>
                    {[
                      { label: 'Image bannière / héro', sublabel: 'Grande photo derrière le slogan (plein écran)', url: heroImageUrl, setUrl: setHeroImageUrl, uploading: uploadingHero, onUpload: handleHeroImageUpload, inputRef: heroImageInputRef, inputId: 'hero-upload' },
                      { label: 'Image "À propos"', sublabel: 'Affiché côté droit de la section À propos', url: aboutImageUrl, setUrl: setAboutImageUrl, uploading: uploadingAbout, onUpload: handleAboutImageUpload, inputRef: aboutImageInputRef, inputId: 'about-upload' },
                      { label: 'Photo galerie / bâtiment', sublabel: 'Affiché dans la section galerie intermédiaire', url: galleryImageUrl, setUrl: setGalleryImageUrl, uploading: uploadingGallery, onUpload: handleGalleryImageUpload, inputRef: galleryImageInputRef, inputId: 'gallery-upload' },
                    ].map(({ label, sublabel, url, setUrl, uploading, onUpload, inputId }) => (
                      <div key={inputId} className="p-3 bg-gray-50 rounded-xl border">
                        <p className="text-sm font-semibold text-gray-700">{label}</p>
                        <p className="text-xs text-gray-400 mb-2">{sublabel}</p>
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => onUpload(e.target.files[0])} className="hidden" id={inputId} />
                        <div className="flex gap-2 items-center">
                          <label htmlFor={inputId} className="inline-flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-700 rounded-lg cursor-pointer text-xs border transition-colors">
                            <Upload className="w-3.5 h-3.5" />
                            {uploading ? 'Chargement...' : url ? 'Changer' : 'Télécharger'}
                          </label>
                          {url && <button onClick={() => setUrl('')} className="text-xs text-red-500 hover:underline">Supprimer</button>}
                        </div>
                        {url && <img src={url} alt={label} className="mt-2 rounded-lg h-20 w-full object-cover border" onError={e => e.target.style.display='none'} />}
                      </div>
                    ))}
                  </div>

                  {/* Police */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Police</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[{value:'inter',label:'Inter',sub:'Moderne'},{value:'playfair',label:'Playfair',sub:'Luxe / Serif'},{value:'raleway',label:'Raleway',sub:'Élégant'},{value:'montserrat',label:'Montserrat',sub:'Premium'}].map(f => (
                        <button key={f.value} onClick={() => setFontChoice(f.value)}
                          className={`p-3 rounded-xl border-2 text-center transition-all ${fontChoice===f.value?'border-blue-500 bg-blue-50':'border-gray-200 hover:border-gray-300'}`}>
                          <p className="font-semibold text-sm">{f.label}</p>
                          <p className="text-xs text-gray-500">{f.sub}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Stats */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Statistiques vitrines (4 chiffres)</label>
                    <p className="text-xs text-gray-400 mb-2">Laissez vide pour utiliser les valeurs par défaut</p>
                    <div className="grid grid-cols-2 gap-3">
                      {customStats.map((stat, i) => (
                        <div key={i} className="flex gap-2">
                          <input className="w-20 border rounded px-2 py-1.5 text-sm text-center font-bold focus:ring-1 focus:ring-blue-400 focus:outline-none" value={stat.value} onChange={e => { const s=[...customStats]; s[i]={...s[i],value:e.target.value}; setCustomStats(s); }} placeholder="25+" />
                          <input className="flex-1 border rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none" value={stat.label} onChange={e => { const s=[...customStats]; s[i]={...s[i],label:e.target.value}; setCustomStats(s); }} placeholder="Ans d'expérience" />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pourquoi nous */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Nos 3 engagements (section "Pourquoi nous choisir")</label>
                    <div className="space-y-3">
                      {whyUs.map((item, i) => (
                        <div key={i} className="grid grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg">
                          <input className="col-span-3 sm:col-span-1 border rounded px-2 py-1.5 text-sm font-semibold bg-white focus:ring-1 focus:ring-blue-400 focus:outline-none" value={item.title} onChange={e => { const w=[...whyUs]; w[i]={...w[i],title:e.target.value}; setWhyUs(w); }} placeholder={`Titre ${i+1}`} />
                          <input className="col-span-3 sm:col-span-2 border rounded px-2 py-1.5 text-sm bg-white focus:ring-1 focus:ring-blue-400 focus:outline-none" value={item.text} onChange={e => { const w=[...whyUs]; w[i]={...w[i],text:e.target.value}; setWhyUs(w); }} placeholder="Description courte..." />
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>

              <Button onClick={saveBranding} disabled={savingBranding} className="w-full bg-[#0f1d3c] hover:bg-[#162a4f]">
                {savingBranding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Enregistrer toute la personnalisation</>}
              </Button>
            </div>
          )}

          {/* ===== KIT COMMUNICATION (QR + AFFICHES) ===== */}
          {activeTab === 'kit_pub' && (
            <KitPubSection
              branding={{
                primaryColor: selectedColor || '#0f1d3c',
                accentColor: selectedAccentColor || '#F59E0B',
                logoUrl: logoPreview || null,
                agencyName: settings?.company_name || partner?.company_name || partner?.nom || 'Mon Agence',
                phone: contactPhone || '',
                website: websiteUrl || '',
                subdomain: settings?.subdomain || partner?.subdomain || '',
                heroImageUrl: heroImageUrl || '',
                aboutImageUrl: aboutImageUrl || '',
                galleryImageUrl: galleryImageUrl || '',
              }}
              onBack={() => setActiveTab('branding')}
            />
          )}

          {/* ===== EMAILS CLIENTS ===== */}
          {activeTab === 'emails' && (
            <div className="space-y-6" data-testid="partner-emails">
              <div className="flex items-center gap-3">
                <button onClick={() => setActiveTab('overview')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Retour
                </button>
                <Mail className="w-5 h-5 text-blue-600" />
                <h2 className="font-semibold text-lg text-gray-900">Personnalisation des Emails Clients</h2>
              </div>

              <Card>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <p className="font-medium text-gray-800 mb-1">Email de confirmation d'audit gratuit</p>
                    <p className="text-sm text-gray-500 mb-3">
                      Ce message s'affiche dans l'email envoyé à votre client après qu'il ait soumis sa demande d'audit via votre lien.
                      Laissez vide pour utiliser le message par défaut.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <p className="text-xs text-amber-800 font-medium mb-1">Message par défaut :</p>
                      <p className="text-xs text-amber-700 italic">
                        "L'équipe vous contactera dans les meilleurs délais pour analyser votre projet."
                      </p>
                    </div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Votre message personnalisé</label>
                    <textarea
                      data-testid="email-confirmation-text"
                      value={emailConfirmationText}
                      onChange={e => setEmailConfirmationText(e.target.value)}
                      rows={4}
                      maxLength={500}
                      placeholder="Ex : Notre équipe vous recontactera sous 24h pour vous présenter notre analyse complète du bien."
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{emailConfirmationText.length}/500 caractères</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                    <p className="font-medium text-gray-700 mb-2">Aperçu de l'email :</p>
                    <div className="border-l-4 border-emerald-400 pl-3 italic text-gray-600">
                      "{emailConfirmationText || "L'équipe vous contactera dans les meilleurs délais pour analyser votre projet."}"
                    </div>
                  </div>

                  <Button
                    data-testid="save-email-settings-btn"
                    onClick={saveEmailSettings}
                    disabled={savingEmail}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {savingEmail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Enregistrer</>}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ===== PAIEMENTS ===== */}
          {activeTab === 'payments' && (
            <div className="space-y-6" data-testid="partner-payments">
              <div className="flex items-center gap-3">
                <button onClick={() => setActiveTab('overview')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Retour
                </button>
                <h2 className="text-xl font-bold text-gray-900">Paiements</h2>
                <span className="text-lg">{settings.country === 'algerie' ? '🇩🇿' : '🇫🇷'}</span>
              </div>

              {/* === MODE ALGÉRIE + PAYS ÉTRANGERS === */}
              {settings.country && settings.country !== 'france' ? (
                <div className="space-y-4">
                  {/* Info abonnement */}
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        {settings.country === 'algerie' ? 'Abonnement Algérie — 15 000 DZD/mois' :
                         settings.country === 'maroc'   ? 'Abonnement Maroc — 1 000 MAD/mois' :
                         settings.country === 'tunisie' ? 'Abonnement Tunisie — 300 TND/mois' :
                         settings.country === 'senegal' ? 'Abonnement Sénégal — 65 000 CFA/mois' :
                         'Abonnement — Tarif personnalisé'}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Payez par <strong>Virement bancaire</strong> ou <strong>CCP (Centre de Chèques Postaux)</strong> au nom de votre administrateur. Votre abonnement est activé sous 24h après validation.
                      </p>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                        <p className="text-sm font-semibold text-amber-800 mb-1">Coordonnées de paiement</p>
                        <p className="text-sm text-amber-700">Contactez votre administrateur pour obtenir les coordonnées bancaires / CCP.</p>
                      </div>

                      {/* Formulaire déclaration virement */}
                      <h4 className="font-medium text-gray-700 mb-3">Déclarer un paiement effectué</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Type de paiement</label>
                          <div className="flex gap-3">
                            {[{ key: 'virement', label: 'Virement bancaire' }, { key: 'ccp', label: 'CCP' }].map(t => (
                              <button key={t.key} type="button"
                                onClick={() => setAlgeriaPaymentForm(f => ({ ...f, payment_type: t.key }))}
                                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${algeriaPaymentForm.payment_type === t.key ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`}>
                                {t.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Montant versé (DZD)</label>
                          <input type="number" value={algeriaPaymentForm.amount_dzd}
                            onChange={e => setAlgeriaPaymentForm(f => ({ ...f, amount_dzd: parseFloat(e.target.value) || 0 }))}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="15000" />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Référence du virement (optionnel)</label>
                          <input type="text" value={algeriaPaymentForm.reference}
                            onChange={e => setAlgeriaPaymentForm(f => ({ ...f, reference: e.target.value }))}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="N° de reçu, référence CCP..." />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Note (optionnel)</label>
                          <textarea value={algeriaPaymentForm.note}
                            onChange={e => setAlgeriaPaymentForm(f => ({ ...f, note: e.target.value }))}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            rows={2} placeholder="Informations complémentaires..." />
                        </div>
                        <Button
                          onClick={async () => {
                            if (!algeriaPaymentForm.amount_dzd || algeriaPaymentForm.amount_dzd <= 0) { toast.error('Entrez un montant valide'); return; }
                            setSendingAlgeriaPayment(true);
                            try {
                              await axios.post(`${API}/algerie/payment/manual-request`, algeriaPaymentForm, { headers: getHeaders() });
                              toast.success('Demande envoyée ! Votre abonnement sera activé sous 24h après vérification.');
                              setAlgeriaPaymentForm(f => ({ ...f, reference: '', note: '' }));
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
                            } finally { setSendingAlgeriaPayment(false); }
                          }}
                          disabled={sendingAlgeriaPayment}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="submit-algeria-payment-btn"
                        >
                          {sendingAlgeriaPayment ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</> : 'Déclarer ce paiement'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Chargily Pay (placeholder) */}
                  <Card className="opacity-60 border-dashed">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-600" /> Chargily Pay (Bientôt disponible)
                      </h3>
                      <p className="text-sm text-gray-500">Le paiement en ligne sécurisé via Chargily Pay sera disponible prochainement. Utilisez le virement en attendant.</p>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                /* === MODE FRANCE — Stripe === */
                <Card><CardContent className="p-5">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-600" /> Stripe</h3>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 mb-4">
                    <div className={`w-3 h-3 rounded-full ${settings.stripe_configured ? 'bg-green-500' : 'bg-orange-500'}`} />
                    <div className="flex-1"><p className="font-medium text-sm">{settings.stripe_configured ? 'Stripe configuré' : 'Stripe non configuré'}</p>{settings.stripe_configured && <p className="text-xs text-gray-500">Clé : {settings.stripe_key_masked}</p>}</div>
                    {settings.stripe_configured && <Button variant="outline" size="sm" className="text-red-600" onClick={removeStripeKey} data-testid="remove-stripe-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer</Button>}
                  </div>
                  {!settings.stripe_configured && (
                    <div className="space-y-3">
                      <input type="password" value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} placeholder="sk_live_xxxxxxxx" className="w-full border rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="stripe-key-input" />
                      <Button onClick={saveStripeKey} disabled={savingStripe || !stripeKey.trim()} className="bg-[#0f1d3c] hover:bg-[#162a4f]" data-testid="save-stripe-btn">
                        {savingStripe ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Vérification...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Enregistrer</>}
                      </Button>
                    </div>
                  )}
                </CardContent></Card>
              )}
            </div>
          )}

          {activeTab === 'estimation_coefs' && (
            <PartnerEstimationCoefsTab />
          )}

          {/* ===== VITRINE DE BIENS ===== */}
          {activeTab === 'vitrine' && (
            <div className="space-y-6" data-testid="partner-vitrine">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={() => setActiveTab('overview')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Retour
                  </button>
                  <Home className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-xl font-bold text-gray-900">Vitrine de biens</h2>
                </div>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setEditingAnnonce(null); setAnnonceForm({ transaction_type:'vente', property_type:'appartement', zone:'', zone_label:'', city:'', price:'', surface:'', rooms:'', title:'', description:'', contact_phone:'', status:'active' }); setAnnonceFormOpen(true); }} data-testid="add-annonce-btn">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter une annonce
                </Button>
              </div>

              {/* Info page publique */}
              {settings.subdomain && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-3 text-sm">
                  <Globe className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-emerald-700">Vos annonces sont visibles sur votre page publique :</span>
                  <a href={`/p/${settings.subdomain}#vitrine`} target="_blank" rel="noreferrer" className="text-emerald-700 underline font-medium truncate">
                    /p/{settings.subdomain}
                  </a>
                </div>
              )}

              {/* Formulaire ajout / édition */}
              {annonceFormOpen && (
                <Card className="border-2 border-emerald-300">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">{editingAnnonce ? 'Modifier l\'annonce' : 'Nouvelle annonce'}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Transaction type */}
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Type de transaction</label>
                        <div className="flex gap-2">
                          {[{k:'vente',l:'Vente'},{k:'location',l:'Location'}].map(t => (
                            <button key={t.k} type="button" onClick={() => setAnnonceForm(f => ({...f, transaction_type: t.k}))}
                              className={`px-4 py-2 rounded-lg text-sm border transition-colors ${annonceForm.transaction_type === t.k ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`}>
                              {t.l}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* Type de bien */}
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Type de bien</label>
                        <select value={annonceForm.property_type} onChange={e => setAnnonceForm(f => ({...f, property_type: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                          {(algeriaPriceGrid?.property_types || [{key:'appartement',label:'Appartement'},{key:'villa',label:'Villa'},{key:'local_commercial',label:'Local Commercial'},{key:'terrain',label:'Terrain'}]).map(t => (
                            <option key={t.key} value={t.key}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      {/* Zone (Algeria) ou Ville (autres) */}
                      {settings.country === 'algerie' ? (
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Zone</label>
                          <select value={annonceForm.zone} onChange={e => {
                            const z = algeriaPriceGrid?.zones?.find(z => z.key === e.target.value);
                            setAnnonceForm(f => ({...f, zone: e.target.value, zone_label: z?.label || e.target.value}));
                          }} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                            <option value="">Sélectionner une zone</option>
                            {(algeriaPriceGrid?.zones || []).map(z => <option key={z.key} value={z.key}>{z.label}</option>)}
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Ville</label>
                          <input type="text" value={annonceForm.city} onChange={e => setAnnonceForm(f => ({...f, city: e.target.value}))}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Ex: Casablanca, Tunis..." />
                        </div>
                      )}
                      {/* Prix */}
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">
                          Prix ({settings.country === 'algerie' ? 'DZD' : settings.country === 'maroc' ? 'MAD' : settings.country === 'tunisie' ? 'TND' : 'devise locale'})
                        </label>
                        <input type="number" value={annonceForm.price} onChange={e => setAnnonceForm(f => ({...f, price: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Ex: 12000000" />
                      </div>
                      {/* Surface */}
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Surface (m²)</label>
                        <input type="number" value={annonceForm.surface} onChange={e => setAnnonceForm(f => ({...f, surface: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Ex: 80" />
                      </div>
                      {/* Pièces */}
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Nombre de pièces</label>
                        <input type="number" value={annonceForm.rooms} onChange={e => setAnnonceForm(f => ({...f, rooms: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Ex: 3" />
                      </div>
                      {/* Titre */}
                      <div className="sm:col-span-2">
                        <label className="text-sm text-gray-600 mb-1 block">Titre de l'annonce *</label>
                        <input type="text" value={annonceForm.title} onChange={e => setAnnonceForm(f => ({...f, title: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Ex: Appartement F3 vue mer à Oran Centre" />
                      </div>
                      {/* Description */}
                      <div className="sm:col-span-2">
                        <label className="text-sm text-gray-600 mb-1 block">Description</label>
                        <textarea value={annonceForm.description} onChange={e => setAnnonceForm(f => ({...f, description: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" rows={3} placeholder="Décrivez votre bien..." />
                      </div>
                      {/* Téléphone contact */}
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Téléphone contact</label>
                        <input type="tel" value={annonceForm.contact_phone} onChange={e => setAnnonceForm(f => ({...f, contact_phone: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="0550 00 00 00" />
                      </div>
                      {/* Statut */}
                      {editingAnnonce && (
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Statut</label>
                          <select value={annonceForm.status} onChange={e => setAnnonceForm(f => ({...f, status: e.target.value}))}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                            <option value="active">Active</option>
                            <option value="vendu">Vendu</option>
                            <option value="loue">Loué</option>
                            <option value="archive">Archivée</option>
                          </select>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 mt-4 justify-end">
                      <Button variant="outline" onClick={() => setAnnonceFormOpen(false)}>Annuler</Button>
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={savingAnnonce || !annonceForm.title || !annonceForm.price}
                        onClick={async () => {
                          setSavingAnnonce(true);
                          try {
                            const payload = { ...annonceForm, price: parseInt(annonceForm.price) || 0, surface: parseFloat(annonceForm.surface) || null, rooms: parseInt(annonceForm.rooms) || null };
                            if (editingAnnonce) {
                              await axios.put(`${API}/partenaire/annonces/${editingAnnonce.id}`, payload, { headers: getHeaders() });
                              toast.success('Annonce mise à jour');
                            } else {
                              await axios.post(`${API}/partenaire/annonces`, payload, { headers: getHeaders() });
                              toast.success('Annonce publiée !');
                            }
                            const res = await axios.get(`${API}/partenaire/annonces`, { headers: getHeaders() });
                            setAnnonces(res.data || []);
                            setAnnonceFormOpen(false);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Erreur');
                          } finally { setSavingAnnonce(false); }
                        }}
                        data-testid="save-annonce-btn"
                      >
                        {savingAnnonce ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</> : <><CheckCircle className="w-4 h-4 mr-2" />{editingAnnonce ? 'Mettre à jour' : 'Publier'}</>}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Liste des annonces */}
              {annonces.length === 0 ? (
                <Card><CardContent className="p-12 text-center">
                  <Home className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">Aucune annonce publiée</p>
                  <p className="text-gray-400 text-sm mt-1">Ajoutez votre première annonce pour la faire apparaître sur votre page publique.</p>
                </CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {annonces.map(ann => (
                    <Card key={ann.id} className={`overflow-hidden ${ann.status !== 'active' ? 'opacity-60' : ''}`} data-testid={`annonce-card-${ann.id}`}>
                      {/* Photo principale */}
                      {ann.photos?.length > 0 ? (
                        <div className="h-40 bg-gray-100 relative overflow-hidden">
                          <img src={ann.photos[0]} alt={ann.title} className="w-full h-full object-cover" />
                          <div className="absolute top-2 left-2 flex gap-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${ann.transaction_type === 'vente' ? 'bg-blue-600' : 'bg-purple-600'}`}>
                              {ann.transaction_type === 'vente' ? 'Vente' : 'Location'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="h-28 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center relative">
                          <Home className="w-10 h-10 text-emerald-300" />
                          <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold text-white ${ann.transaction_type === 'vente' ? 'bg-blue-600' : 'bg-purple-600'}`}>
                            {ann.transaction_type === 'vente' ? 'Vente' : 'Location'}
                          </span>
                          {/* Upload photo */}
                          <label className="absolute bottom-2 right-2 cursor-pointer">
                            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                              const file = e.target.files[0]; if (!file) return;
                              setUploadingPhoto(true);
                              try {
                                const fd = new FormData(); fd.append('file', file);
                                await axios.post(`${API}/partenaire/annonces/${ann.id}/photo`, fd, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
                                const res = await axios.get(`${API}/partenaire/annonces`, { headers: getHeaders() });
                                setAnnonces(res.data || []);
                                toast.success('Photo ajoutée');
                              } catch { toast.error('Erreur upload'); } finally { setUploadingPhoto(false); }
                            }} />
                            <span className="bg-white/90 text-xs text-gray-600 px-2 py-1 rounded shadow hover:bg-white transition">+ Photo</span>
                          </label>
                        </div>
                      )}
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{ann.title}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{ann.zone_label || ann.city || '—'}</p>
                          </div>
                          <Badge className={ann.status === 'active' ? 'bg-emerald-100 text-emerald-700' : ann.status === 'vendu' ? 'bg-blue-100 text-blue-700' : ann.status === 'loue' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}>
                            {ann.status === 'active' ? 'Active' : ann.status === 'vendu' ? 'Vendu' : ann.status === 'loue' ? 'Loué' : 'Archivée'}
                          </Badge>
                        </div>
                        <div className="flex gap-3 mt-2 text-sm text-gray-600">
                          {ann.surface && <span>{ann.surface} m²</span>}
                          {ann.rooms && <span>{ann.rooms} pièces</span>}
                        </div>
                        <p className="text-emerald-700 font-bold text-lg mt-1">
                          {parseInt(ann.price).toLocaleString('fr-FR')} {settings.country === 'algerie' ? 'DZD' : settings.country === 'maroc' ? 'MAD' : settings.country === 'tunisie' ? 'TND' : ''}
                          {ann.transaction_type === 'location' && '/mois'}
                        </p>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" className="text-xs flex-1" onClick={() => {
                            setEditingAnnonce(ann);
                            setAnnonceForm({ transaction_type: ann.transaction_type, property_type: ann.property_type, zone: ann.zone||'', zone_label: ann.zone_label||'', city: ann.city||'', price: ann.price||'', surface: ann.surface||'', rooms: ann.rooms||'', title: ann.title||'', description: ann.description||'', contact_phone: ann.contact_phone||'', status: ann.status||'active' });
                            setAnnonceFormOpen(true);
                            window.scrollTo({top: 0, behavior:'smooth'});
                          }} data-testid={`edit-annonce-${ann.id}`}>Modifier</Button>
                          {/* Upload photo si annonce a déjà une photo */}
                          {ann.photos?.length > 0 && (
                            <label className="flex-1 cursor-pointer">
                              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                const file = e.target.files[0]; if (!file) return;
                                setUploadingPhoto(true);
                                try {
                                  const fd = new FormData(); fd.append('file', file);
                                  await axios.post(`${API}/partenaire/annonces/${ann.id}/photo`, fd, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
                                  const res = await axios.get(`${API}/partenaire/annonces`, { headers: getHeaders() });
                                  setAnnonces(res.data || []);
                                  toast.success('Photo ajoutée');
                                } catch { toast.error('Erreur upload'); } finally { setUploadingPhoto(false); }
                              }} />
                              <span className="w-full inline-flex items-center justify-center px-3 py-1.5 border rounded-md text-xs text-gray-700 bg-white hover:bg-gray-50 transition">
                                {uploadingPhoto ? <Loader2 className="w-3 h-3 animate-spin" /> : '+ Photo'}
                              </span>
                            </label>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs" onClick={async () => {
                            if (!window.confirm('Supprimer cette annonce ?')) return;
                            await axios.delete(`${API}/partenaire/annonces/${ann.id}`, { headers: getHeaders() });
                            setAnnonces(a => a.filter(x => x.id !== ann.id));
                            toast.success('Annonce supprimée');
                          }} data-testid={`delete-annonce-${ann.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== MON DOMAINE ===== */}
          {activeTab === 'domaine' && (
            <PartnerDomainTab getHeaders={getHeaders} />
          )}

          {activeTab === 'purge' && (
            <div className="space-y-6" data-testid="partner-purge">
              <div className="flex items-center gap-3">
                <button onClick={() => setActiveTab('overview')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Retour
                </button>
                <Trash2 className="w-5 h-5 text-red-600" />
                <div>
                  <h2 className="font-semibold text-lg text-gray-900">Purge des données</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Supprimez vos données de test avant la mise en production. Irréversible.</p>
                </div>
                <Button size="sm" variant="outline" className="ml-auto text-xs" onClick={loadPurgePreview}>
                  Actualiser les compteurs
                </Button>
              </div>

              {/* Table par collection */}
              <Card>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-600">Collection</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-600 w-24">Documents</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-600 w-32">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purgePreview.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 text-center text-gray-400 text-xs italic">
                            Cliquez sur "Actualiser les compteurs" pour voir vos données
                          </td>
                        </tr>
                      ) : purgePreview.map(col => (
                        <tr key={col.key} className="border-t">
                          <td className="px-4 py-3 font-medium text-gray-800">{col.label}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-bold ${col.count > 0 ? 'text-red-600' : 'text-gray-400'}`}>{col.count}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50 text-xs h-7"
                              disabled={col.count === 0 || purgingKey === col.key}
                              onClick={() => purgeOneCollection(col.key)}
                              data-testid={`purge-col-${col.key}`}
                            >
                              {purgingKey === col.key ? '...' : 'Supprimer'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Tout purger en une fois
                </h4>
                <p className="text-xs text-amber-700 mb-3">Tapez <code className="bg-white px-1 rounded font-bold text-red-600">SUPPRIMER TOUT</code> pour activer le bouton :</p>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={purgeConfirmText}
                    onChange={e => setPurgeConfirmText(e.target.value)}
                    placeholder="SUPPRIMER TOUT"
                    className="flex-1 border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:border-red-400 focus:outline-none bg-white"
                    data-testid="partner-purge-confirm-input"
                  />
                  <Button
                    variant="destructive"
                    onClick={handlePurgeProjects}
                    disabled={purging || purgeConfirmText !== 'SUPPRIMER TOUT'}
                    data-testid="partner-purge-projects-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {purging ? 'Suppression...' : 'Tout purger'}
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {mobileSidebarOpen && (
        <div style={{position:'fixed',inset:0,zIndex:9999}} className="lg:hidden">
          <div style={{position:'absolute',inset:0,backgroundColor:'rgba(0,0,0,0.5)'}} onClick={() => setMobileSidebarOpen(false)} />
          <div style={{position:'absolute',top:0,left:0,bottom:0,width:'280px',zIndex:10000,backgroundColor:'white',boxShadow:'0 25px 50px -12px rgba(0,0,0,0.25)',overflowY:'auto'}} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 bg-[#0f1d3c] text-white">
              <span className="text-sm font-bold">Navigation</span>
              <button onClick={() => setMobileSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
            </div>
            <PartnerSidebar activeTab={activeTab} onTabChange={setActiveTab} mobileOpen={true} onMobileClose={() => setMobileSidebarOpen(false)} badges={{ hotline: unreadMessages, chantiers: stats.total_chantiers, conseil: stats.total_conseil }} domainEnabled={!!partner?.domain_enabled} isInternational={!!(settings.country && settings.country !== 'france')} />
          </div>
        </div>
      )}

      {/* Project Communication Modal — messagerie + documents par dossier */}
      <Dialog open={!!commProject} onOpenChange={(o) => !o && setCommProject(null)}>
        <DialogContent className="max-w-3xl bg-[#0f1d3c] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-purple-400" />
              Dossier · {commProject?.address}
              <span className="text-xs font-normal text-white/50 ml-2">#{commProject?.id?.slice(0, 8).toUpperCase()}</span>
            </DialogTitle>
          </DialogHeader>
          {commProject && (
            <ProjectCommunication projectId={commProject.id} token={localStorage.getItem('partner_token')} />
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
